import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFilmContext, SESSION_HOME_BOOTSTRAP_KEY } from '../../contexts/FilmContext';
import { useThemeContext, type ThemeType } from '../../contexts/ThemeContext';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { useBudgetDataCache } from '../../contexts/BudgetDataCacheContext';
import { useTabCache } from '../../contexts/TabCacheContext';
import { useBreakdownTabContext } from '../../contexts/BreakdownTabContext';
import { useBudgetTabContext } from '../../contexts/BudgetTabContext';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import {
  ResourceType,
  GranteeType,
  PermissionRole,
  UserPermissions,
  getPermissionsByResource,
  revokeUserPermission,
  setUserPermission,
} from '../../api/userPermissions';
import { getBudgetsByFilmId, updateBudget, deleteBudget, createBudget, createBudgetItemsBatch, copyBudget, getBudgetById } from '../../api/budget';
import { getBreakdownListByFilmId, updateBreakdown, deleteBreakdown, createBreakdown, createScene, getBreakdownById, createBreakdownCopyJob, getBreakdownCopyJob } from '../../api/breakdown';
import { getSchedulesByBreakdownId } from '../../api/schedule';
import { BreakdownInfo, CreateBreakdownParams, BudgetResponse } from './types';
import { formatCurrencyAmount } from '../Budget/BudgetTable/exchangeRates';
import { appCurrencyCodeToBudgetCurrency } from '../Budget/BudgetTable/utils/currencyConverter';
import { applyUserAppConfig } from '../../utils/userAppConfig';
import type { AppUiStyle } from '../../types/userAppConfig';
import { generateBudgetId, generateBudgetItemId } from '../../utils/generateId';
import { DEFAULT_BUDGET_TEMPLATE, getTranslatedBudgetItemsByLevel } from '../Budget/BudgetTable/Templates/budgetTemplate';
import ProgressDialog from '../../components/UI/ProgressDialog/ProgressDialog';
import FormDialog from '../../components/UI/FormDialog';
import axios from '../../api/axios';
import { ReactComponent as CardBreakdownIcon } from '../../assets/svg/Home/card_breakdown.svg';
import { ReactComponent as CardBudgetIcon } from '../../assets/svg/Home/card_budget.svg';
import { CreateSceneParams, SceneDataStatus } from '../../types/scene.types';
import saveIcon from '../../assets/images/Scene/save.png';
import saveNoIcon from '../../assets/images/Scene/save_no.png';
import { useGlobalUsers } from '../../contexts/GlobalUsersContext';
import { resetBatchUsersQueryCoordinator } from '../../hooks/useBatchUsersQuery';
import DropdownMenu from '../../components/DropdownMenu/DropdownMenu';
import { DropdownMenuItem } from '../../components/DropdownMenu/DropdownMenu';
import { APP_LANGUAGE_OPTIONS } from '../../i18n/languageOptions';
import { Film, updateFilm, deleteFilm, createFilmWithUpload } from '../../api/film';
import { getCurrentUserInfo, getCurrentUserID, getCurrentUserOrgID } from '../../utils/userUtils';
import { parseScopedUserLastUsedEntry, resolveEffectiveScheduleId } from '../../utils/resolveUserLastUsed';
import { UserAccountType, UserSubscriptionType } from '../../types/auth';
import { logout } from '../../api/auth';
import addIcon from '../../assets/images/Scene/add.png';
import { ReactComponent as SettingIcon } from '../../assets/svg/Home/setting.svg';
import { ReactComponent as FilmIcon } from '../../assets/svg/Home/film.svg';
import { ReactComponent as EditIcon } from '../../assets/svg/Home/edit.svg';
import { ReactComponent as DeleteIcon } from '../../assets/svg/Home/delete.svg';
import { ReactComponent as ShareIcon } from '../../assets/svg/Home/share.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/Home/download.svg';
import { ReactComponent as AddIcon } from '../../assets/svg/Home/add.svg';
import { ReactComponent as CopyIcon } from '../../assets/svg/Home/copy.svg';
import { ReactComponent as WaitIcon } from '../../assets/svg/Home/wait.svg';
import { ReactComponent as BudgetIcon } from '../../assets/svg/Home/budget.svg';
import { ReactComponent as ExitIcon } from '../../assets/svg/Home/exit.svg';
import CreateFilmModal from './components/CreateFilmModal';
import PermissionUserSearch from '../../components/PermissionUserSearch/PermissionUserSearch';
import './Home.css';

type FilmUserSearchAction = 'share' | 'changeOwner';

const THEME_COLORS: Record<ThemeType, string> = {
  dark: '#2d2c3a',
  blue: '#6F65C6',
  green: '#097717',
  orange: '#E9632C',
};

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedFilmID,
    selectedFilmName,
    setSelectedFilm,
    clearSelectedFilm,
    films,
    filmsLoading,
    setFilms,
    refreshFilmListFromPermissions,
    currentFilm: currentFilmBundle,
    currentFilmChildrenLoading,
    homeDataHydrated,
    bootstrapAfterLogin,
    selectFilm,
    appendSceneToCurrentFilm,
    appendBudgetToCurrentFilm,
    patchSceneInCurrentFilm,
    reloadCurrentFilmChildren,
  } = useFilmContext();
  const { theme, setTheme } = useThemeContext();
  const { clearAllCache: clearBreakdownCache } = useDataCache();
  const { clearAllCache: clearBudgetCache } = useBudgetDataCache();
  const { clearAllCache: clearTabCache } = useTabCache();
  const { setBreakdownId, setActiveTab: setBreakdownActiveTab } = useBreakdownTabContext();
  const { setBudgetId, setActiveTab: setBudgetActiveTab } = useBudgetTabContext();
  const {
    clearUserCache,
    setServerSyncedField: setUserBehaviorField,
    localOnlyData,
    serverSyncedData,
    getUserCache,
    getResolvedUserAppConfig,
    updateUserAppConfig,
    flushUserAppConfigSync,
  } = useCurrentUser();
  const { clearAllPermissions, getPermissions, setPermissions } = useUserPermissions();
  const { getUser, clearAllUsers } = useGlobalUsers();
  const createBreakdownHandlerRef = useRef<(() => void) | null>(null);
  const createBudgetHandlerRef = useRef<(() => Promise<void>) | null>(null);
  
  // 标签状态管理 - 根据影片项目类型自动决定显示分场列表还是预算列表
  const [activeTab, setActiveTab] = useState<'breakdown' | 'budget' | null>(null);
  
  // 筛选标签状态：所有、我的分场/预算、我参与的、分享给我的
  const [filterTab, setFilterTab] = useState<'all' | 'mine' | 'participated' | 'shared'>('all');
  
  // 影片项目筛选标签状态：所有、我的、我参与的、分享给我的
  const [filmFilterTab, setFilmFilterTab] = useState<'all' | 'mine' | 'participated' | 'shared'>('all');
  
  const currentFilm = currentFilmBundle?.film ?? null;
  
  // 影片编辑状态
  const [isEditingFilm, setIsEditingFilm] = useState(false);
  const [filmEditData, setFilmEditData] = useState<Partial<Film>>({});
  const [editingFilmField, setEditingFilmField] = useState<string | null>(null);
  const [editingFieldValue, setEditingFieldValue] = useState<string>('');
  const [activeFilmUserSearch, setActiveFilmUserSearch] = useState<FilmUserSearchAction | null>(null);
  const [filmCollaboratorPermissions, setFilmCollaboratorPermissions] = useState<UserPermissions[]>([]);

  useEffect(() => {
    setActiveFilmUserSearch(null);
    setFilmCollaboratorPermissions([]);
  }, [selectedFilmID, isEditingFilm]);

  useEffect(() => {
    if (!activeFilmUserSearch || !selectedFilmID) return;
    let cancelled = false;
    void getPermissionsByResource(ResourceType.FILM, selectedFilmID)
      .then((response) => {
        if (cancelled) return;
        const permissions = response.data || [];
        setFilmCollaboratorPermissions(permissions);
        setPermissions(ResourceType.FILM, selectedFilmID, permissions);
      })
      .catch((error) => {
        console.warn('加载影片协作者失败:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [activeFilmUserSearch, selectedFilmID, setPermissions]);
  
  // 创建影片模态框状态
  const [isCreateFilmModalOpen, setIsCreateFilmModalOpen] = useState(false);
  
  // 影视类型下拉菜单状态
  const [filmTypeMenuVisible, setFilmTypeMenuVisible] = useState(false);
  const filmTypeButtonRef = useRef<HTMLButtonElement>(null);
  const [filmTypeMenuPosition, setFilmTypeMenuPosition] = useState({ top: 0, left: 0 });
  const [filmTypeMenuWidth, setFilmTypeMenuWidth] = useState<number | undefined>(undefined);
  
  // 影片类型选项（id 为中文供 API 使用，label 使用 i18n）
  const filmTypeKeyMap: Record<string, string> = {
    '电影': 'movie', '电视剧': 'tv', '动漫': 'anime', '短剧': 'short',
    '纪录片': 'documentary', '微电影': 'micro', '短片': 'shortFilm', '广告': 'ad'
  };
  const filmTypes = ['电影', '电视剧', '动漫', '短剧', '纪录片', '微电影', '短片', '广告'] as const;
  const filmTypeItems: DropdownMenuItem[] = filmTypes.map(type => ({
    id: type,
    label: t(`film.filmTypes.${filmTypeKeyMap[type] || type}`)
  }));
  
  // 用户信息相关状态
  const [userInfo, setUserInfo] = useState<any>(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  
  // 更多设置相关状态
  const [showMoreSettings, setShowMoreSettings] = useState(false);
  const [moreSettingsTab, setMoreSettingsTab] = useState<'setting' | 'about'>('setting');
  const [languageMenuVisible, setLanguageMenuVisible] = useState(false);
  const [currencyMenuVisible, setCurrencyMenuVisible] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('CNY');
  const [uiDensity, setUiDensity] = useState<AppUiStyle>('compact');
  const languageButtonRef = useRef<HTMLDivElement>(null);
  const currencyButtonRef = useRef<HTMLDivElement>(null);
  const [languageMenuPosition, setLanguageMenuPosition] = useState({ top: 0, left: 0 });
  const [currencyMenuPosition, setCurrencyMenuPosition] = useState({ top: 0, left: 0 });
  
  // 分场排序相关状态 - 默认按分场版本降序排序
  const [breakdownSortKey, setBreakdownSortKey] = useState<'breakdownUpdateTime' | 'breakdownName'>('breakdownName');
  const [breakdownSortDirection, setBreakdownSortDirection] = useState<'asc' | 'desc'>('desc');
  const [breakdownSortMenuVisible, setBreakdownSortMenuVisible] = useState(false);
  const breakdownSortButtonRef = useRef<HTMLButtonElement>(null);
  const [breakdownSortButtonPosition, setBreakdownSortButtonPosition] = useState({ top: 0, left: 0 });
  const [breakdownSortButtonWidth, setBreakdownSortButtonWidth] = useState<number | undefined>(undefined);

  // 预算排序相关状态 - 默认按预算名称（预算版本）降序排序
  const [budgetSortKey, setBudgetSortKey] = useState<'budgetUpdateTime' | 'budgetLevel' | 'budgetName'>('budgetName');
  const [budgetSortDirection, setBudgetSortDirection] = useState<'asc' | 'desc'>('desc');
  const [budgetSortMenuVisible, setBudgetSortMenuVisible] = useState(false);
  const budgetSortButtonRef = useRef<HTMLButtonElement>(null);
  const [budgetSortButtonPosition, setBudgetSortButtonPosition] = useState({ top: 0, left: 0 });
  const [budgetSortButtonWidth, setBudgetSortButtonWidth] = useState<number | undefined>(undefined);

  // 分场排序选项
  const breakdownSortOptions: DropdownMenuItem[] = [
    { id: 'breakdownUpdateTime', label: t('home.breakdownUpdateTime') },
    { id: 'breakdownName', label: t('home.breakdownName') },
  ];

  // 预算排序选项
  const budgetSortOptions: DropdownMenuItem[] = [
    { id: 'budgetUpdateTime', label: t('home.budgetUpdateTime') },
    { id: 'budgetLevel', label: t('home.budgetLevel') },
    { id: 'budgetName', label: t('home.budgetName') },
  ];

  // 更新分场下拉菜单位置和宽度
  useEffect(() => {
    if (breakdownSortMenuVisible && breakdownSortButtonRef.current) {
      const rect = breakdownSortButtonRef.current.getBoundingClientRect();
      setBreakdownSortButtonPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
      setBreakdownSortButtonWidth(rect.width);
    }
  }, [breakdownSortMenuVisible]);

  // 更新预算下拉菜单位置和宽度
  useEffect(() => {
    if (budgetSortMenuVisible && budgetSortButtonRef.current) {
      const rect = budgetSortButtonRef.current.getBoundingClientRect();
      setBudgetSortButtonPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
      setBudgetSortButtonWidth(rect.width);
    }
  }, [budgetSortMenuVisible]);

  // 处理分场排序字段选择
  const handleBreakdownSortFieldSelect = (item: DropdownMenuItem) => {
    const newSortKey = item.id as 'breakdownUpdateTime' | 'breakdownName';
    if (newSortKey !== breakdownSortKey) {
      setBreakdownSortKey(newSortKey);
      setBreakdownSortDirection('desc');
    }
    setBreakdownSortMenuVisible(false);
  };

  // 处理预算排序字段选择
  const handleBudgetSortFieldSelect = (item: DropdownMenuItem) => {
    const newSortKey = item.id as 'budgetUpdateTime' | 'budgetLevel' | 'budgetName';
    if (newSortKey !== budgetSortKey) {
      setBudgetSortKey(newSortKey);
      setBudgetSortDirection('desc');
    }
    setBudgetSortMenuVisible(false);
  };

  // 获取分场排序字段显示文本
  const getBreakdownSortFieldLabel = () => {
    const option = breakdownSortOptions.find(opt => opt.id === breakdownSortKey);
    return option?.label || t('home.updateTimeDefault');
  };

  // 获取预算排序字段显示文本
  const getBudgetSortFieldLabel = () => {
    const option = budgetSortOptions.find(opt => opt.id === budgetSortKey);
    return option?.label || t('home.updateTimeDefault');
  };

  // 获取排序方向显示文本
  const getSortDirectionLabel = (direction: 'asc' | 'desc') => {
    return direction === 'asc' ? t('home.ascending') : t('home.descending');
  };

  // 加载用户信息并应用 userAppConfig
  useEffect(() => {
    try {
      const user = getCurrentUserInfo();
      setUserInfo(user);
      const config = getResolvedUserAppConfig(i18n.language);
      setSelectedCurrency(config.currency);
      setUiDensity(config.style);
      applyUserAppConfig(config, { i18n, setTheme });
    } catch (error) {
      console.error('获取用户信息失败:', error);
    }
  }, [getResolvedUserAppConfig, i18n, setTheme]);

  const userPermsOnFilm = useCallback(
    (filmID: string) => {
      const uid = getCurrentUserID();
      return (getPermissions(ResourceType.FILM, filmID) || []).filter(
        (p) => p.granteeType === GranteeType.USER && p.granteeID === uid
      );
    },
    [getPermissions]
  );

  const currentUserOwnsFilm = Boolean(
    currentFilm && currentFilm.filmOwnerID === getCurrentUserID()
  );
  const currentFilmPermissionRole = currentFilm
    ? userPermsOnFilm(currentFilm.filmID)[0]?.role
    : undefined;
  const canDeleteCurrentFilm =
    currentUserOwnsFilm || currentFilmPermissionRole === PermissionRole.ADMIN;
  const canManageCurrentFilmMembers =
    currentUserOwnsFilm || currentFilmPermissionRole === PermissionRole.ADMIN;
  const canEditCurrentFilm =
    currentUserOwnsFilm ||
    currentFilmPermissionRole === PermissionRole.ADMIN ||
    currentFilmPermissionRole === PermissionRole.EDITOR;

  // 首次进入 / 登录后：引导加载权限影片列表；从子页返回且已 hydrated 则不再请求
  useEffect(() => {
    const pending = sessionStorage.getItem(SESSION_HOME_BOOTSTRAP_KEY) === '1';
    if ((location.state as { fromLogin?: boolean } | null)?.fromLogin) {
      sessionStorage.setItem(SESSION_HOME_BOOTSTRAP_KEY, '1');
    }
    const shouldBootstrap =
      pending ||
      (!homeDataHydrated && !filmsLoading && films.length === 0);
    if (!shouldBootstrap) return;
    sessionStorage.removeItem(SESSION_HOME_BOOTSTRAP_KEY);
    if ((location.state as { fromLogin?: boolean } | null)?.fromLogin) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    void bootstrapAfterLogin();
  }, [
    homeDataHydrated,
    filmsLoading,
    films.length,
    bootstrapAfterLogin,
    location.state,
    location.pathname,
    navigate,
  ]);

  // 根据筛选标签过滤影片列表（UserPermissionsContext 已为每部影片缓存权限）
  const filteredFilms = useMemo(() => {
    let result: Film[] = [];
    const uid = getCurrentUserID();

    if (filmFilterTab === 'all') {
      result = films.filter(
        (film) => film.filmOwnerID === uid || userPermsOnFilm(film.filmID).length > 0
      );
    } else if (filmFilterTab === 'mine') {
      result = films.filter((film) => film.filmOwnerID === uid);
    } else if (filmFilterTab === 'participated') {
      result = films.filter((film) =>
        userPermsOnFilm(film.filmID).some(
          (p) => p.role === PermissionRole.ADMIN || p.role === PermissionRole.EDITOR
        )
      );
    } else if (filmFilterTab === 'shared') {
      result = films.filter((film) =>
        userPermsOnFilm(film.filmID).some(
          (p) => p.role === PermissionRole.COPY || p.role === PermissionRole.VIEWER
        )
      );
    } else {
      result = films;
    }

    return result.sort((a, b) => {
      const timeA = a.filmUpdateTime ? new Date(a.filmUpdateTime).getTime() : 0;
      const timeB = b.filmUpdateTime ? new Date(b.filmUpdateTime).getTime() : 0;
      return timeB - timeA;
    });
  }, [films, filmFilterTab, userPermsOnFilm]);

  // 获取用户名首字母（用于头像显示）
  const getUserInitial = () => {
    if (!userInfo) return 'U';
    const name = userInfo.userNickName || userInfo.userName || '';
    if (!name) return 'U';
    // 如果是中文，取第一个字符
    if (/[\u4e00-\u9fa5]/.test(name)) {
      return name.charAt(0);
    }
    // 如果是英文，取首字母大写
    return name.charAt(0).toUpperCase();
  };

  // 获取用户类型显示文本
  const getUserTypeText = () => {
    if (!userInfo?.userAccountType) return t('home.personalUser');
    const accountType = userInfo.userAccountType;
    if (accountType === UserAccountType.ORGANIZATION_PAID) {
      return t('home.teamUser');
    }
    return t('home.personalUser');
  };

  // 格式化日期
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '-';
    }
  };

  // 获取订阅版本
  const getSubscriptionVersion = () => {
    if (!userInfo?.userAccountType) return '免费版';
    const accountType = userInfo.userAccountType;
    if (accountType === UserAccountType.PERSONAL_FREE) {
      return '免费版';
    }
    // 占位：暂时都显示免费版，后续根据实际订阅信息判断
    return '免费版';
  };

  // 处理头像点击（切换用户详情显示）
  const handleAvatarClick = () => {
    setShowUserDetail(!showUserDetail);
  };

  // 处理关闭用户详情
  const handleCloseUserDetail = () => {
    setShowUserDetail(false);
  };

  // 语言字符映射
  const getLanguageChar = (code: string): string => {
    const charMap: Record<string, string> = {
      'zh-CN': '简',
      'zh-HK': '繁',
      'en': 'EN'
    };
    return charMap[code] || '简';
  };

  // 语言名称映射
  const getLanguageName = (code: string): string => {
    const nameMap: Record<string, string> = {
      'zh-CN': '中文简体',
      'zh-HK': '中文繁体',
      'en': 'English'
    };
    return nameMap[code] || '中文简体';
  };

  const getCurrencySymbol = (currencyCode: string): string => {
    const symbolMap: Record<string, string> = {
      CNY: '¥',
      HKD: 'HK$',
      TWD: 'NT$',
      USD: '$',
      JPY: '¥',
      KRW: '₩',
      GBP: '£',
      EUR: '€',
      THB: '฿',
    };
    return symbolMap[currencyCode] || currencyCode.charAt(0);
  };

  const syncHomeSettingsFromUserAppConfig = useCallback(() => {
    const cache = getUserCache();
    if (!cache?.user) return;
    const config = getResolvedUserAppConfig(i18n.language);
    setSelectedCurrency(config.currency);
    setUiDensity(config.style);
    applyUserAppConfig(config, { i18n, setTheme });
  }, [getUserCache, getResolvedUserAppConfig, i18n, setTheme]);

  useEffect(() => {
    if (showMoreSettings) {
      syncHomeSettingsFromUserAppConfig();
    }
  }, [showMoreSettings, syncHomeSettingsFromUserAppConfig]);

  // 更新菜单位置
  useEffect(() => {
    if (languageMenuVisible && languageButtonRef.current) {
      const rect = languageButtonRef.current.getBoundingClientRect();
      setLanguageMenuPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [languageMenuVisible]);

  useEffect(() => {
    if (currencyMenuVisible && currencyButtonRef.current) {
      const rect = currencyButtonRef.current.getBoundingClientRect();
      setCurrencyMenuPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [currencyMenuVisible]);

  // 更新影视类型下拉菜单位置和宽度
  useEffect(() => {
    if (filmTypeMenuVisible && filmTypeButtonRef.current) {
      const rect = filmTypeButtonRef.current.getBoundingClientRect();
      setFilmTypeMenuPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
      setFilmTypeMenuWidth(rect.width);
    }
  }, [filmTypeMenuVisible]);

  // 界面语言选项（与登录、注册页统一）
  const languageOptions = APP_LANGUAGE_OPTIONS;

  const getCurrencyDisplayName = (currencyCode: string) =>
    t(`home.currency.${currencyCode || 'CNY'}`);

  const currencyOptions: DropdownMenuItem[] = [
    { id: 'CNY', label: t('home.currency.CNY') },
    { id: 'HKD', label: t('home.currency.HKD') },
    { id: 'TWD', label: t('home.currency.TWD') },
    { id: 'USD', label: t('home.currency.USD') },
    { id: 'JPY', label: t('home.currency.JPY') },
    { id: 'KRW', label: t('home.currency.KRW') },
    { id: 'GBP', label: t('home.currency.GBP') },
    { id: 'EUR', label: t('home.currency.EUR') },
    { id: 'THB', label: t('home.currency.THB') },
  ];

  const handleLanguageSelect = (item: DropdownMenuItem) => {
    const lang = item.id as string;
    i18n.changeLanguage(lang);
    updateUserAppConfig({ language: lang });
    setLanguageMenuVisible(false);
  };

  const handleCurrencySelect = (item: DropdownMenuItem) => {
    const code = item.id as string;
    setSelectedCurrency(code);
    updateUserAppConfig({ currency: code as import('../../types/userAppConfig').AppCurrencyCode });
    setCurrencyMenuVisible(false);
  };

  const handleUiDensityChange = (style: AppUiStyle) => {
    setUiDensity(style);
    document.documentElement.setAttribute('data-ui-density', style);
    updateUserAppConfig({ style });
  };

  const handleThemeSelect = (themeKey: ThemeType) => {
    setTheme(themeKey);
    updateUserAppConfig({ theme: themeKey });
  };

  // 处理更多设置标题点击
  const handleMoreSettingsToggle = () => {
    if (showMoreSettings) {
      flushUserAppConfigSync();
    }
    setShowMoreSettings(!showMoreSettings);
  };

  // 处理关闭更多设置
  const handleCloseMoreSettings = () => {
    flushUserAppConfigSync();
    setShowMoreSettings(false);
  };

  const rawLang = i18n.language || 'zh-CN';
  const currentLanguage = rawLang.startsWith('zh-HK') ? 'zh-HK' : rawLang.startsWith('zh') ? 'zh-CN' : 'en';

  // 处理退出登录
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('退出登录失败:', error);
    } finally {
      // 登出：清空业务与权限缓存，保证下次登录从干净状态开始
      clearBreakdownCache();
      clearBudgetCache();
      clearTabCache();
      clearAllPermissions();
      resetBatchUsersQueryCoordinator();
      clearAllUsers();
      clearSelectedFilm();
      setFilms([]);
      setBreakdownId(null);
      setBudgetId(null);
      setBreakdownActiveTab('breakdown');
      setBudgetActiveTab('budget');
      try {
        sessionStorage.removeItem(SESSION_HOME_BOOTSTRAP_KEY);
      } catch {
        /* ignore */
      }
      clearUserCache();
      localStorage.removeItem('tabs');
      navigate('/login');
    }
  };

  // 处理影片项目选择（同步 FilmContext、子数据与 userLastUsedFilmID）
  const handleFilmSelect = async (film: Film) => {
    try {
      setIsEditingFilm(false);
      setFilmEditData({});
      await selectFilm(film.filmID);

      if (film.filmProjectType === '分场项目') {
        setActiveTab('breakdown');
      } else if (film.filmProjectType === '预算项目') {
        setActiveTab('budget');
      } else {
        if (showBreakdownTab) {
          setActiveTab('breakdown');
        } else if (showBudgetTab) {
          setActiveTab('budget');
        }
      }
    } catch (e) {
      console.error('切换影片失败:', e);
    }
  };

  // 处理字段点击编辑
  const handleFieldClick = (field: keyof Film) => {
    if (currentFilm && canEditCurrentFilm) {
      setEditingFilmField(field);
      const fieldValue = currentFilm[field];
      // 只处理字符串类型的字段
      if (typeof fieldValue === 'string') {
        setEditingFieldValue(fieldValue);
      } else {
        setEditingFieldValue('');
      }
      // 如果是影视类型字段，直接打开下拉菜单
      if (field === 'filmType') {
        // 使用 setTimeout 确保在 DOM 更新后打开菜单
        setTimeout(() => {
          setFilmTypeMenuVisible(true);
        }, 10);
      }
    }
  };

  // 处理字段保存
  const handleFieldSave = async (field: keyof Film, value?: string) => {
    if (!selectedFilmID || !currentFilm || !canEditCurrentFilm) return;

    const valueToSave = value !== undefined ? value : editingFieldValue;

    try {
      const updateData = { [field]: valueToSave };
      await updateFilm(selectedFilmID, updateData);

      setFilms((prevFilms) =>
        prevFilms.map((film) => (film.filmID === selectedFilmID ? { ...film, ...updateData } : film))
      );

      if (field === 'filmName' && valueToSave !== currentFilm.filmName) {
        setSelectedFilm(selectedFilmID, valueToSave as string, currentFilm.filmStartShootingDate || null);
      }

      void reloadCurrentFilmChildren();

      setEditingFilmField(null);
      setEditingFieldValue('');
    } catch (error: any) {
      console.error('更新影片字段失败:', error);
      alert(`${t('schedule.updateFailed')}${error?.response?.data?.message || error?.message || t('common.unknownError')}`);
    }
  };

  // 处理字段取消编辑
  const handleFieldCancel = () => {
    setEditingFilmField(null);
    setEditingFieldValue('');
  };

  // 处理取消编辑（保留用于影片名称编辑）
  const handleCancelEdit = () => {
    setIsEditingFilm(false);
    setFilmEditData({});
  };

  // 处理保存影片信息
  const handleSaveFilm = async () => {
    if (!selectedFilmID || !currentFilm) return;

    try {
      await updateFilm(selectedFilmID, filmEditData);

      setFilms((prevFilms) =>
        prevFilms.map((film) => (film.filmID === selectedFilmID ? { ...film, ...filmEditData } : film))
      );

      if (filmEditData.filmName && filmEditData.filmName !== currentFilm.filmName) {
        setSelectedFilm(selectedFilmID, filmEditData.filmName, currentFilm.filmStartShootingDate || null);
      }

      void reloadCurrentFilmChildren();

      setIsEditingFilm(false);
      setFilmEditData({});
    } catch (error: any) {
      console.error('更新影片失败:', error);
      alert(`${t('schedule.updateFailed')}${error?.response?.data?.message || error?.message || t('common.unknownError')}`);
    }
  };

  const handleFilmUserSelect = useCallback(
    async (
      action: FilmUserSearchAction,
      userID: string | null,
      role?: Exclude<PermissionRole, PermissionRole.OWNER>,
      removeAllCollaborators = false,
      previousOwnerRole: Exclude<PermissionRole, PermissionRole.OWNER> | 'remove' = PermissionRole.ADMIN,
    ) => {
      if (!selectedFilmID || !userID) return;
      const creatorID = getCurrentUserID();
      try {
        if (action === 'changeOwner') {
          const previousOwnerID = currentFilm?.filmOwnerID;
          const permissionsToRemove = removeAllCollaborators
            ? filmCollaboratorPermissions
            : filmCollaboratorPermissions.filter(permission => permission.granteeID === userID);
          await Promise.all(permissionsToRemove.map(permission => revokeUserPermission({
            resourceType: ResourceType.FILM,
            resourceID: selectedFilmID,
            granteeType: permission.granteeType,
            granteeID: permission.granteeID,
          })));
          let previousOwnerPermission = null;
          if (previousOwnerID && previousOwnerRole !== 'remove') {
            const permissionResponse = await setUserPermission({
              resourceType: ResourceType.FILM,
              resourceID: selectedFilmID,
              granteeType: GranteeType.USER,
              granteeID: previousOwnerID,
              role: previousOwnerRole,
              creatorID: previousOwnerID,
            });
            previousOwnerPermission = permissionResponse.data;
          }
          await updateFilm(selectedFilmID, { filmOwnerID: userID });
          const nextPermissions = [
            ...(removeAllCollaborators ? [] : filmCollaboratorPermissions.filter(permission =>
              permission.granteeID !== userID && permission.granteeID !== previousOwnerID
            )),
            ...(previousOwnerPermission ? [previousOwnerPermission] : []),
          ];
          setFilmCollaboratorPermissions(nextPermissions);
          setPermissions(ResourceType.FILM, selectedFilmID, nextPermissions);
          await selectFilm(selectedFilmID);
        } else {
          const permRes = await setUserPermission({
            resourceType: ResourceType.FILM,
            resourceID: selectedFilmID,
            granteeType: GranteeType.USER,
            granteeID: userID,
            role: role || PermissionRole.VIEWER,
            creatorID,
          });
          if (permRes.data) {
            const existing = filmCollaboratorPermissions;
            const nextPermissions = [
              ...existing.filter(
                (p) => !(p.granteeType === GranteeType.USER && p.granteeID === userID)
              ),
              permRes.data,
            ];
            setFilmCollaboratorPermissions(nextPermissions);
            setPermissions(ResourceType.FILM, selectedFilmID, nextPermissions);
          }
        }
      } catch (error: unknown) {
        console.error('影片用户操作失败:', error);
      } finally {
        if (action === 'changeOwner') setActiveFilmUserSearch(null);
      }
    },
    [selectedFilmID, currentFilm?.filmOwnerID, selectFilm, filmCollaboratorPermissions, setPermissions]
  );

  const handleFilmPermissionRoleChange = useCallback(
    async (permission: { granteeID: string }, role: Exclude<PermissionRole, PermissionRole.OWNER>) => {
      if (!selectedFilmID) return;
      try {
        const response = await setUserPermission({
          resourceType: ResourceType.FILM,
          resourceID: selectedFilmID,
          granteeType: GranteeType.USER,
          granteeID: permission.granteeID,
          role,
          creatorID: getCurrentUserID(),
        });
        const nextPermissions = [
          ...filmCollaboratorPermissions.filter((item) => item.granteeID !== permission.granteeID),
          response.data,
        ];
        setFilmCollaboratorPermissions(nextPermissions);
        setPermissions(ResourceType.FILM, selectedFilmID, nextPermissions);
      } catch (error) {
        console.error('变更影片协作者权限失败:', error);
      }
    },
    [filmCollaboratorPermissions, selectedFilmID, setPermissions]
  );

  const handleFilmPermissionRemove = useCallback(async (granteeID: string) => {
    if (!selectedFilmID) return;
    try {
      await revokeUserPermission({
        resourceType: ResourceType.FILM,
        resourceID: selectedFilmID,
        granteeType: GranteeType.USER,
        granteeID,
      });
      const nextPermissions = filmCollaboratorPermissions.filter((item) => item.granteeID !== granteeID);
      setFilmCollaboratorPermissions(nextPermissions);
      setPermissions(
        ResourceType.FILM,
        selectedFilmID,
        nextPermissions,
      );
    } catch (error) {
      console.error('移除影片协作者失败:', error);
    }
  }, [filmCollaboratorPermissions, selectedFilmID, setPermissions]);

  const renderFilmUserActionButton = (
    action: FilmUserSearchAction,
    labelKey: 'home.shareFilm' | 'home.changeOwner',
    Icon: React.FC<React.SVGProps<SVGSVGElement>>
  ) => (
    <div className="film-action-btn-wrap" key={action}>
      <button
        className={`film-action-btn${activeFilmUserSearch === action ? ' is-active' : ''}`}
        type="button"
        title={t(labelKey)}
        onClick={() => setActiveFilmUserSearch((prev) => (prev === action ? null : action))}
      >
        <Icon className="film-action-icon" />
        <span>{t(labelKey)}</span>
      </button>
      {activeFilmUserSearch === action && (
        <div className="film-user-search-wrapper">
          <PermissionUserSearch
            value={action === 'changeOwner' ? currentFilm?.filmOwnerID ?? null : null}
            ownerID={currentFilm?.filmOwnerID ?? null}
            permissions={selectedFilmID ? filmCollaboratorPermissions : []}
            onChange={(userID, role) => void handleFilmUserSelect(action, userID, role)}
            onTransferOwnership={action === 'share' && currentUserOwnsFilm
              ? (userID, previousRole, removeAll) => void handleFilmUserSelect('changeOwner', userID, undefined, removeAll, previousRole)
              : undefined}
            onRoleChange={action === 'share' ? (permission, role) => {
              void handleFilmPermissionRoleChange(permission, role);
            } : undefined}
            onRemove={action === 'share' ? (permission) => {
              void handleFilmPermissionRemove(permission.granteeID);
            } : undefined}
            visible={activeFilmUserSearch === action}
            onClose={() => setActiveFilmUserSearch(null)}
          />
        </div>
      )}
    </div>
  );

  // 处理删除影片
  const handleDeleteFilm = async () => {
    if (!selectedFilmID || !currentFilm) return;
    
    if (!window.confirm(`确定要删除影片"${currentFilm.filmName}"吗？此操作不可恢复。`)) {
      return;
    }

    try {
      await deleteFilm(selectedFilmID);
      const list = await refreshFilmListFromPermissions();
      if (list.length > 0) {
        const sorted = [...list].sort((a, b) => {
          const timeA = a.filmUpdateTime ? new Date(a.filmUpdateTime).getTime() : 0;
          const timeB = b.filmUpdateTime ? new Date(b.filmUpdateTime).getTime() : 0;
          return timeB - timeA;
        });
        await selectFilm(sorted[0].filmID);
      } else {
        clearSelectedFilm();
      }
    } catch (error: any) {
      console.error('删除影片失败:', error);
      alert(`${t('schedule.deleteFailed')}${error?.response?.data?.message || error?.message || t('common.unknownError')}`);
    }
  };

  // 处理创建影片
  const handleCreateFilm = async (filmData: any) => {
    try {
      // 验证必填字段
      if (!filmData.filmName || filmData.filmName.trim() === '') {
        alert(t('home.filmNameRequired'));
        return;
      }

      const userInfo = getCurrentUserInfo();

      // 创建FormData对象
      const formData = new FormData();
      
      // 生成 filmID：FILM_ + 10 位随机大写字母和数字
      const FILM_ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      const filmID =
        'FILM_' +
        Array.from({ length: 10 }, () =>
          FILM_ID_CHARS.charAt(Math.floor(Math.random() * FILM_ID_CHARS.length))
        ).join('');
      
      // 添加字段到FormData
      formData.append('filmID', filmID);
      formData.append('filmName', filmData.filmName);
      if (filmData.filmType) formData.append('filmType', filmData.filmType);
      if (filmData.filmProjectType) formData.append('filmProjectType', filmData.filmProjectType);
      if (userInfo.userOrgID) formData.append('filmOrgID', userInfo.userOrgID);
      if (userInfo.userID) formData.append('filmOwnerID', userInfo.userID);
      if (filmData.executiveProducer) formData.append('filmProducer', filmData.executiveProducer);
      if (filmData.director) formData.append('filmDirector', filmData.director);
      if (filmData.screenwriter) formData.append('filmScreenwriter', filmData.screenwriter);
      if (filmData.filmSynopsis) formData.append('filmSynopsis', filmData.filmSynopsis);

      const response = await createFilmWithUpload(formData);
      
      if (response.data) {
        await refreshFilmListFromPermissions();
        setIsCreateFilmModalOpen(false);
        const newFilm = response.data;
        if (newFilm?.filmID) {
          await selectFilm(newFilm.filmID);
        }
      }
    } catch (error: any) {
      console.error('创建影片失败:', error);
      const errorMessage = error?.response?.data?.message || error?.message || t('common.unknownError');
      alert(`${t('film.filmCreateFailed')}${errorMessage}`);
    }
  };
  
  // 根据用户订阅信息判断是否显示标签
  const showBreakdownTab = userInfo?.userSubscriptionBreakdownVersion && userInfo.userSubscriptionBreakdownVersion !== UserSubscriptionType.UNSUBSCRIBED;
  const showBudgetTab = userInfo?.userSubscriptionBudgetsVersion && userInfo.userSubscriptionBudgetsVersion !== UserSubscriptionType.UNSUBSCRIBED;

  // ========== 预算和分场数据状态（与 FilmContext.currentFilm 同步，本地可乐观更新） ==========
  const [budgets, setBudgets] = useState<BudgetResponse[]>([]);
  const budgetsLoading = !!selectedFilmID && currentFilmChildrenLoading;
  const [budgetsError, setBudgetsError] = useState<string | null>(null);
  
  // 预算编辑状态
  const [editingBudgetId, setEditingBudgetId] = useState<string | null>(null);
  const [editingBudgetName, setEditingBudgetName] = useState<string>('');
  const [budgetNameError, setBudgetNameError] = useState<string | null>(null);
  const [showDeleteBudgetConfirm, setShowDeleteBudgetConfirm] = useState<string | null>(null);

  // 分场数据状态
  interface BreakdownWithOwnerInfo extends BreakdownInfo {
    ownerInfo?: {
      userNickname: string;
      userAvatar: string;
    };
  }

  const [breakdown, setBreakdown] = useState<BreakdownWithOwnerInfo[]>([]);
  const breakdownLoading = !!selectedFilmID && currentFilmChildrenLoading;
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  
  // 分场编辑状态
  const [editingBreakdownId, setEditingBreakdownId] = useState<string | null>(null);
  const [editingBreakdownName, setEditingBreakdownName] = useState<string>('');
  const [editingOverviewId, setEditingOverviewId] = useState<string | null>(null);
  const [editingOverview, setEditingOverview] = useState<string>('');
  const [breakdownNameError, setBreakdownNameError] = useState<string | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [showDeleteBreakdownConfirm, setShowDeleteBreakdownConfirm] = useState<string | null>(null);
  const [copyingBreakdownId, setCopyingBreakdownId] = useState<string | null>(null);
  const [copyBreakdownStatusMessage, setCopyBreakdownStatusMessage] = useState<string>('');
  const [copyingBudgetId, setCopyingBudgetId] = useState<string | null>(null);
  const [copyProgress, setCopyProgress] = useState<number>(0);

  // 创建对话框状态
  const [showCreateBreakdownDialog, setShowCreateBreakdownDialog] = useState(false);
  const [createBreakdownFormMessage, setCreateBreakdownFormMessage] = useState<string>('');
  
  // 进度对话框状态（用于预算创建）
  const [progressDialogVisible, setProgressDialogVisible] = useState<boolean>(false);
  const [progressDialogMessage, setProgressDialogMessage] = useState<string>('');
  const [progressDialogProgress, setProgressDialogProgress] = useState<number>(0);

  // 输入框 refs
  const budgetNameInputRef = useRef<HTMLInputElement>(null);
  const breakdownNameInputRef = useRef<HTMLInputElement>(null);
  const overviewInputRef = useRef<HTMLInputElement>(null);
  
  // 根据当前影片项目类型和订阅信息决定应该显示哪个标签
  useEffect(() => {
    if (!currentFilm) {
      return;
    }
    
    // 如果影片有项目类型，根据项目类型设置（优先使用项目类型）
    if (currentFilm.filmProjectType === '分场项目') {
      setActiveTab('breakdown');
    } else if (currentFilm.filmProjectType === '预算项目') {
      setActiveTab('budget');
    } else if (activeTab === null) {
      // 如果没有设置项目类型且 activeTab 为 null，根据用户订阅情况决定
      if (showBreakdownTab) {
        setActiveTab('breakdown');
      } else if (showBudgetTab) {
        setActiveTab('budget');
      }
    }
  }, [currentFilm, showBreakdownTab, showBudgetTab, activeTab]);

  const userPermsOnBudget = useCallback(
    (budgetID: string) => {
      const uid = getCurrentUserID();
      return (getPermissions(ResourceType.BUDGET, budgetID) || []).filter(
        (p) => p.granteeType === GranteeType.USER && p.granteeID === uid
      );
    },
    [getPermissions]
  );

  const userPermsOnBreakdownResource = useCallback(
    (breakdownID: string) => {
      const uid = getCurrentUserID();
      return (getPermissions(ResourceType.BREAKDOWN, breakdownID) || []).filter(
        (p) => p.granteeType === GranteeType.USER && p.granteeID === uid
      );
    },
    [getPermissions]
  );

  const getBreakdownCardCapabilities = useCallback((item: BreakdownInfo) => {
    if (item.breakdownOwnerID === getCurrentUserID()) {
      return { canEdit: true, canCopy: true, canDelete: true };
    }
    const role = userPermsOnBreakdownResource(item.breakdownID)[0]?.role;
    return {
      canEdit: role === PermissionRole.ADMIN || role === PermissionRole.EDITOR,
      canCopy:
        role === PermissionRole.ADMIN ||
        role === PermissionRole.EDITOR ||
        role === PermissionRole.COPY,
      canDelete: role === PermissionRole.ADMIN,
    };
  }, [userPermsOnBreakdownResource]);

  useEffect(() => {
    if (!currentFilmBundle || currentFilmBundle.film.filmID !== selectedFilmID) {
      setBudgets([]);
      setBreakdown([]);
      setBudgetsError(null);
      setBreakdownError(null);
      return;
    }
    setBudgets(currentFilmBundle.budgets);
    const raw = currentFilmBundle.breakdown;
    setBreakdown(
      raw.map((bd: BreakdownInfo) => ({
        ...bd,
        ownerInfo: bd.breakdownOwnerID
          ? (() => {
              const user = getUser(bd.breakdownOwnerID);
              return user
                ? {
                    userNickname: user.userNickName || user.userName || '',
                    userAvatar: user.userAvatar || '',
                  }
                : undefined;
            })()
          : undefined,
      }))
    );
  }, [currentFilmBundle, selectedFilmID, getUser]);

  // ========== 筛选和排序逻辑 ==========
  // 筛选后的预算列表
  const filteredBudgets = useMemo(() => {
    let filtered = budgets;

    const currentUserID = getCurrentUserID();
    if (filterTab === 'all') {
      filtered = budgets.filter(
        (budget) => budget.budgetOwnerID === currentUserID || userPermsOnBudget(budget.budgetID).length > 0
      );
    } else if (filterTab === 'mine') {
      filtered = budgets.filter(budget => budget.budgetOwnerID === currentUserID);
    } else if (filterTab === 'participated') {
      filtered = budgets.filter((budget) =>
        userPermsOnBudget(budget.budgetID).some(
          (p) => p.role === PermissionRole.ADMIN || p.role === PermissionRole.EDITOR
        )
      );
    } else if (filterTab === 'shared') {
      filtered = budgets.filter((budget) =>
        userPermsOnBudget(budget.budgetID).some(
          (p) => p.role === PermissionRole.COPY || p.role === PermissionRole.VIEWER
        )
      );
    }
    
    const sorted = [...filtered].sort((a: BudgetResponse, b: BudgetResponse) => {
      let comparison = 0;
      
      if (budgetSortKey === 'budgetUpdateTime') {
        const timeA = new Date(a.budgetUpdateTime).getTime();
        const timeB = new Date(b.budgetUpdateTime).getTime();
        comparison = timeA - timeB;
      } else if (budgetSortKey === 'budgetLevel') {
        const levelOrder: Record<string, number> = { '总预算': 1, '分预算': 2 };
        const levelA = levelOrder[a.budgetLevel] || 999;
        const levelB = levelOrder[b.budgetLevel] || 999;
        comparison = levelA - levelB;
      } else if (budgetSortKey === 'budgetName') {
        const nameA = (a.budgetName || '').trim();
        const nameB = (b.budgetName || '').trim();
        if (!nameA && !nameB) return 0;
        if (!nameA) return 1;
        if (!nameB) return -1;
        comparison = nameA.localeCompare(nameB, 'zh-CN');
      }
      
      return budgetSortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [budgets, filterTab, budgetSortKey, budgetSortDirection, userPermsOnBudget]);

  // 筛选后的分场列表
  const filteredBreakdown = useMemo(() => {
    const currentUserID = getCurrentUserID();
    if (filterTab === 'all') {
      return breakdown.filter(
        (bd) => bd.breakdownOwnerID === currentUserID || userPermsOnBreakdownResource(bd.breakdownID).length > 0
      );
    } else if (filterTab === 'mine') {
      return breakdown.filter((bd) => bd.breakdownOwnerID === currentUserID);
    } else if (filterTab === 'participated') {
      return breakdown.filter((bd) =>
        userPermsOnBreakdownResource(bd.breakdownID).some(
          (p) => p.role === PermissionRole.ADMIN || p.role === PermissionRole.EDITOR
        )
      );
    } else if (filterTab === 'shared') {
      return breakdown.filter((bd) =>
        userPermsOnBreakdownResource(bd.breakdownID).some(
          (p) => p.role === PermissionRole.COPY || p.role === PermissionRole.VIEWER
        )
      );
    }
    return breakdown;
  }, [breakdown, filterTab, userPermsOnBreakdownResource]);

  // 排序后的分场列表
  const sortedBreakdown = useMemo(() => {
    const sorted = [...filteredBreakdown];
    
    if (breakdownSortKey === 'breakdownUpdateTime') {
      sorted.sort((a, b) => {
        const timeA = a.breakdownUpdateTime ? new Date(a.breakdownUpdateTime).getTime() : 0;
        const timeB = b.breakdownUpdateTime ? new Date(b.breakdownUpdateTime).getTime() : 0;
        if (timeA === 0 && timeB === 0) return 0;
        if (timeA === 0) return 1;
        if (timeB === 0) return -1;
        return breakdownSortDirection === 'asc' ? timeA - timeB : timeB - timeA;
      });
    } else if (breakdownSortKey === 'breakdownName') {
      sorted.sort((a, b) => {
        const nameA = (a.breakdownName || '').trim();
        const nameB = (b.breakdownName || '').trim();
        if (!nameA && !nameB) return 0;
        if (!nameA) return 1;
        if (!nameB) return -1;
        const comparison = nameA.localeCompare(nameB, 'zh-CN');
        return breakdownSortDirection === 'asc' ? comparison : -comparison;
      });
    }
    
    return sorted;
  }, [filteredBreakdown, breakdownSortKey, breakdownSortDirection]);

  // ========== 编辑和删除处理函数 ==========
  // 预算编辑处理
  const handleBudgetEditClick = (e: React.MouseEvent, budgetID: string, budgetName: string) => {
    e.stopPropagation();
    setEditingBudgetId(budgetID);
    setEditingBudgetName(budgetName);
    setBudgetNameError(null);
    setTimeout(() => {
      budgetNameInputRef.current?.focus();
    }, 0);
  };

  const handleBudgetSave = async (budgetID: string) => {
    if (!editingBudgetName.trim()) {
      setBudgetNameError(t('home.budgetNameRequired'));
      return;
    }

    const isDuplicate = budgets.some(budget => 
      budget.budgetID !== budgetID && 
      budget.budgetName.trim() === editingBudgetName.trim()
    );

    if (isDuplicate) {
      setBudgetNameError('已有相同版本的预算，请重新命名');
      return;
    }

    try {
      await updateBudget(budgetID, { budgetName: editingBudgetName.trim() });
      
      setBudgets(prev => prev.map(budget => 
        budget.budgetID === budgetID 
          ? { ...budget, budgetName: editingBudgetName.trim() }
          : budget
      ));
      
      setEditingBudgetId(null);
      setEditingBudgetName('');
      setBudgetNameError(null);
    } catch (error) {
      console.error('保存预算信息失败:', error);
    }
  };

  const handleBudgetDelete = async (budgetID: string) => {
    try {
      await deleteBudget(budgetID);
      setBudgets(prev => prev.filter(budget => budget.budgetID !== budgetID));
      setShowDeleteBudgetConfirm(null);
    } catch (error) {
      console.error('删除预算失败:', error);
      alert(t('home.deleteBudgetFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  // 分场编辑处理
  const handleBreakdownEditClick = (e: React.MouseEvent, breakdownID: string, breakdownName: string, breakdownScriptOverview?: string) => {
    e.stopPropagation();
    setEditingBreakdownId(breakdownID);
    setEditingBreakdownName(breakdownName);
    setEditingOverviewId(breakdownID);
    setEditingOverview(breakdownScriptOverview || '');
    setBreakdownNameError(null);
    setOverviewError(null);
    
    setTimeout(() => {
      breakdownNameInputRef.current?.focus();
    }, 0);
  };

  const handleBreakdownSave = async (breakdownID: string) => {
    if (!editingBreakdownName.trim()) {
      setBreakdownNameError(t('home.breakdownNameRequired'));
      return;
    }

    const isDuplicate = breakdown.some(bd => 
      bd.breakdownID !== breakdownID && 
      bd.breakdownName.trim() === editingBreakdownName.trim()
    );

    if (isDuplicate) {
      setBreakdownNameError('已有相同版本的分场，请重新命名');
      return;
    }

    try {
      const updateData: any = { breakdownName: editingBreakdownName.trim() };
      if (editingOverview.trim()) {
        updateData.breakdownScriptOverview = editingOverview.trim();
      }
      
      const res = await updateBreakdown(breakdownID, updateData);
      if (res.data) {
        patchSceneInCurrentFilm(res.data);
      }

      setEditingBreakdownId(null);
      setEditingBreakdownName('');
      setEditingOverviewId(null);
      setEditingOverview('');
      setBreakdownNameError(null);
      setOverviewError(null);
    } catch (error) {
      console.error('保存分场信息失败:', error);
    }
  };

  const handleBreakdownDelete = async (breakdownID: string) => {
    try {
      await deleteBreakdown(breakdownID);
      setBreakdown(prev => prev.filter(bd => bd.breakdownID !== breakdownID));
      await reloadCurrentFilmChildren();
      setShowDeleteBreakdownConfirm(null);
    } catch (error) {
      console.error('删除分场失败:', error);
      alert(t('home.deleteBreakdownFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  // 预算点击处理（数据由 /budgets 页 useBudgetDataLoader 拉取并写入缓存）
  const handleBudgetClick = useCallback(
    (budget: BudgetResponse) => {
      setBudgetId(budget.budgetID);
      setBudgetActiveTab('budget');
      navigate('/budgets', { replace: true });
    },
    [navigate, setBudgetId, setBudgetActiveTab]
  );

  // 分场点击处理（数据由各业务路由页 useBreakdownDataLoader 等拉取；回主页已全清缓存）
  const handleSceneClick = useCallback(
    async (breakdownID: string, _breakdownName: string) => {
      setUserBehaviorField('userLastUsedBreakdownID', breakdownID);

      let defaultTab: 'breakdown' | 'schedule' | 'element-manager' = 'breakdown';
      const userLastUsedBreakdownTab = localOnlyData.userLastUsedBreakdownTab;

      if (userLastUsedBreakdownTab && Array.isArray(userLastUsedBreakdownTab) && userLastUsedBreakdownTab.length > 0) {
        const tabCache = userLastUsedBreakdownTab.find((item) => {
          const parts = item.split(';');
          return parts.length >= 2 && parts[0] === breakdownID;
        });

        if (tabCache) {
          const parts = tabCache.split(';');
          const tabIdentifier = parts[1];

          if (tabIdentifier === 'breakdown-tab') {
            defaultTab = 'breakdown';
          } else if (tabIdentifier === 'schedule-tab' || tabIdentifier === 'slot-tab') {
            defaultTab = 'schedule';
          } else if (tabIdentifier === 'element-manager-tab') {
            defaultTab = 'element-manager';
          }
        }
      }

      setBreakdownId(breakdownID);
      setBreakdownActiveTab(defaultTab);

      if (defaultTab === 'breakdown') {
        navigate(`/breakdown/${breakdownID}`, { replace: true });
        return;
      }
      if (defaultTab === 'element-manager') {
        const rememberedScheduleId = parseScopedUserLastUsedEntry(
          serverSyncedData.userLastUsedScheduleID,
          breakdownID,
        );
        navigate(
          rememberedScheduleId
            ? `/elements/${breakdownID}?schedule=${encodeURIComponent(rememberedScheduleId)}`
            : `/elements/${breakdownID}`,
          { replace: true },
        );
        return;
      }

      try {
        const res = await getSchedulesByBreakdownId(breakdownID);
        const list = res.data || [];
        const sid = resolveEffectiveScheduleId(
          serverSyncedData.userLastUsedScheduleID,
          breakdownID,
          list,
        );
        if (sid) {
          navigate(`/schedule/${sid}`, { replace: true });
        } else {
          navigate(`/breakdown/${breakdownID}`, { replace: true });
        }
      } catch {
        navigate(`/breakdown/${breakdownID}`, { replace: true });
      }
    },
    [
      setUserBehaviorField,
      localOnlyData,
      setBreakdownId,
      setBreakdownActiveTab,
      navigate,
      serverSyncedData.userLastUsedScheduleID,
    ],
  );

  // ========== 创建处理函数 ==========
  // 创建默认预算项的函数
  const createDefaultBudgetItems = useCallback(
    async (budgetID: string, userID: string, budgetItemCurrency: string) => {
    try {
      const createdItems = new Map<string, string>();
      const allBudgetItems: any[] = [];
      const maxLevel = Math.max(...DEFAULT_BUDGET_TEMPLATE.map(item => item.budgetItemLevel));
      for (let level = 1; level <= maxLevel; level++) {
        const levelItems = getTranslatedBudgetItemsByLevel(t, level);
        for (const templateItem of levelItems) {
          const budgetItemID = generateBudgetItemId();
          let parentID: string | null = null;
          if (templateItem.budgetItemParentCode) {
            parentID = createdItems.get(templateItem.budgetItemParentCode) || null;
          }
          const params: any = {
            budgetItemID,
            budgetItemCode: templateItem.budgetItemCode,
            budgetItemName: templateItem.budgetItemName,
            budgetItemBelongBudgetID: budgetID,
            budgetItemLevel: templateItem.budgetItemLevel - 1,
            budgetItemRole: '可申请可拨发',
            budgetItemProgressType: '按时间进度',
            budgetItemOwnerID: userID,
            budgetItemAssigneeID: userID,
            budgetItemCreatorID: userID,
            budgetItemAmount: 0,
            budgetItemRemark: '',
            budgetItemUsedAmount: 0,
            budgetItemUnusedAmount: 0,
            budgetItemExceedingAmount: 0,
            budgetItemDataStatus: '正常',
            budgetItemStatus: '预算中',
            budgetItemCurrency,
            budgetItemUnit1: templateItem.budgetItemUnit1 || undefined,
            budgetItemUnit2: templateItem.budgetItemUnit2 || undefined,
            budgetItemQuantity1: templateItem.budgetItemQuantity1 || undefined,
            budgetItemQuantity2: templateItem.budgetItemQuantity2 || undefined,
          };
          if (parentID) {
            params.budgetItemParentID = parentID;
          }
          allBudgetItems.push(params);
          createdItems.set(templateItem.budgetItemCode, budgetItemID);
        }
      }
      
      let BATCH_SIZE = 50;
      if (allBudgetItems.length > 200) {
        BATCH_SIZE = 25;
      }
      const batches = [];
      for (let i = 0; i < allBudgetItems.length; i += BATCH_SIZE) {
        batches.push(allBudgetItems.slice(i, i + BATCH_SIZE));
      }
      
      let successCount = 0;
      let failureCount = 0;
      
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        try {
          await createBudgetItemsBatch(batch);
          successCount += batch.length;
          const progress = Math.round(10 + ((i + 1) / batches.length) * 75);
          setProgressDialogProgress(progress);
          if (i < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (batchError) {
          console.error(`第 ${i + 1} 批次创建失败:`, batchError);
          failureCount += batch.length;
          continue;
        }
      }
      
      if (failureCount > 0) {
        if (successCount === 0) {
          throw new Error(`所有默认预算项创建失败，共 ${failureCount} 个项目失败`);
        }
      }
      
      setProgressDialogProgress(95);
      await new Promise(resolve => setTimeout(resolve, 200));
      
      try {
        const verifyResponse = await axios.get(`/api/budgetItems?budgetItemBelongBudgetID=${budgetID}`);
        const loadedItems = verifyResponse.data;
        if (loadedItems.length === 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (verifyError) {
        console.error('验证加载预算项失败，但继续执行:', verifyError);
      }
      
      setProgressDialogProgress(100);
    } catch (error) {
      console.error('创建默认预算项失败:', error);
      if (error instanceof Error && error.message.includes('413')) {
        throw new Error('默认预算项数据量过大，请联系管理员调整服务器配置');
      } else if (error instanceof Error && error.message.includes('网络')) {
        throw new Error('网络连接失败，请检查网络后重试');
      } else {
        throw error;
      }
    }
  },
    [t],
  );

  const handleCreateBudget = useCallback(async () => {
    try {
      setProgressDialogVisible(true);
      setProgressDialogMessage(t('budget.createProgress'));
      setProgressDialogProgress(0);
      
      const userID = getCurrentUserID();
      const newBudgetID = generateBudgetId();
      const now = new Date().toISOString();
      const budgetCurrency = appCurrencyCodeToBudgetCurrency(
        getResolvedUserAppConfig(i18n.language).currency,
      );

      setProgressDialogProgress(5);
      const response = await createBudget({
        budgetID: newBudgetID,
        budgetName: t('budget.untitled'),
        budgetFilmID: selectedFilmID || undefined,
        budgetOwnerID: userID,
        budgetAssigneeID: userID,
        budgetCreatorID: userID,
        budgetStatus: '预算中',
        budgetStorageMode: '同步',
        budgetDataStatus: t('budget.status.draft'),
        budgetCurrency,
        budgetTotalAmount: 0,
        budgetCreateTime: now,
        budgetUpdateTime: now
      });

      if (!response.data) {
        throw new Error(t('budget.createFailed'));
      }

      appendBudgetToCurrentFilm(response.data as unknown as BudgetResponse);

      setProgressDialogProgress(10);

      try {
        await createDefaultBudgetItems(newBudgetID, userID, budgetCurrency);
      } catch (itemError) {
        console.error('创建默认预算项失败，但预算表创建成功:', itemError);
        setProgressDialogVisible(false);
      }

      setTimeout(() => {
        setProgressDialogVisible(false);
      }, 300);

      setBudgetId(newBudgetID);
      setBudgetActiveTab('budget');
      navigate('/budgets', { replace: true });
    } catch (err) {
      console.error('创建预算失败:', err);
      setProgressDialogVisible(false);
      alert(t('home.createBudgetFailed') + (err instanceof Error ? err.message : String(err)));
    }
  }, [
    t,
    selectedFilmID,
    setBudgetId,
    setBudgetActiveTab,
    navigate,
    createDefaultBudgetItems,
    appendBudgetToCurrentFilm,
    getResolvedUserAppConfig,
    i18n.language,
  ]);

  // 暴露创建预算的handler给父组件
  useEffect(() => {
    if (createBudgetHandlerRef.current === null) {
      createBudgetHandlerRef.current = handleCreateBudget;
    }
  }, [handleCreateBudget]);

  // 打开创建分场对话框
  const handleOpenCreateBreakdownDialog = useCallback(() => {
    setShowCreateBreakdownDialog(true);
    setCreateBreakdownFormMessage('');
  }, []);

  // 暴露创建分场的handler给父组件
  useEffect(() => {
    if (createBreakdownHandlerRef.current === null) {
      createBreakdownHandlerRef.current = handleOpenCreateBreakdownDialog;
    }
  }, [handleOpenCreateBreakdownDialog]);

  // 关闭创建分场对话框
  const handleCloseCreateBreakdownDialog = () => {
    setShowCreateBreakdownDialog(false);
    setCreateBreakdownFormMessage('');
  };

  // 执行创建分场的逻辑
  const performCreateBreakdown = async (breakdownName: string, breakdownVersion: string, shouldOpen: boolean = false) => {
    try {
      const userInfo = getCurrentUserInfo();
      if (!userInfo.userID || !userInfo.userOrgID) {
        throw new Error(t('breakdownList.userInfoIncomplete'));
      }

      const createParams: CreateBreakdownParams = {
        breakdownName: breakdownName,
        breakdownScriptOverview: breakdownVersion,
        breakdownOwnerID: userInfo.userID,
        breakdownAssigneeID: userInfo.userID,
        breakdownOrgID: userInfo.userOrgID,
        breakdownFilmID: selectedFilmID || undefined,
        breakdownFilmName: selectedFilmName || undefined,
      };

      const response = await createBreakdown(createParams);
      const newBreakdown = response.data;

      const sceneParams: CreateSceneParams = {
        sceneCode: '1',
        sceneName: `场次 1`,
        sceneBelongBreakdownID: newBreakdown.breakdownID,
        sceneFilmID: selectedFilmID || undefined,
        sceneCreatorID: userInfo.userID,
        sceneDataStatus: SceneDataStatus.NORMAL
      };
      await createScene(sceneParams);

      appendSceneToCurrentFilm(newBreakdown);

      if (shouldOpen) {
        handleSceneClick(newBreakdown.breakdownID, newBreakdown.breakdownName);
      }

      handleCloseCreateBreakdownDialog();
    } catch (error) {
      console.error(t('breakdownList.createBreakdownFailed'), error);
      alert(t('breakdownList.createBreakdownFailed') + t('common.labelSeparator') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  // ========== 复制分场相关函数 ==========
  
  /**
   * 复制分场
   */
  const handleCopyBreakdown = useCallback(async (breakdownID: string) => {
    try {
      setCopyingBreakdownId(breakdownID);
      setCopyProgress(0);
      setCopyBreakdownStatusMessage(t('home.copyBreakdownStages.PREPARING'));

      // 获取当前用户ID和组织ID
      const currentUserID = getCurrentUserID();
      const currentUserOrgID = getCurrentUserOrgID();
      if (!currentUserID) {
        throw new Error('无法获取当前用户信息');
      }
      if (!currentUserOrgID) {
        throw new Error('无法获取用户组织信息');
      }

      // 获取原始分场信息以生成新名称
      const breakdownInfoResponse = await getBreakdownById(breakdownID);
      const breakdownInfo = breakdownInfoResponse.data;
      const originalName = breakdownInfo.breakdownName || '';

      // 生成唯一的分场名称（后缀随界面语言变化）
      let suffixNumber = 1;
      let newBreakdownName = `${originalName}${t('common.copyNameSuffix')}`;

      // 检查当前影片项目下是否已存在同名分场
      if (selectedFilmID) {
        const existingBreakdownResponse = await getBreakdownListByFilmId(selectedFilmID);
        const existingBreakdown = existingBreakdownResponse.data || [];
        const existingNames = new Set(existingBreakdown.map((s: BreakdownInfo) => s.breakdownName));

        while (existingNames.has(newBreakdownName)) {
          suffixNumber++;
          newBreakdownName = `${originalName}${t('common.copyNameSuffixNumbered', { n: suffixNumber })}`;
        }
      }

      const clientRequestID = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `copy-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const createJobPayload = {
        clientRequestID,
        sourceBreakdownID: breakdownID,
        targetBreakdownName: newBreakdownName,
        targetBreakdownOwnerID: currentUserID,
        targetBreakdownAssigneeID: currentUserID,
        targetBreakdownOrgID: currentUserOrgID,
      };

      // 相同幂等键最多重试三次；即使首次响应丢失，后端也只会创建一个副本。
      let response: Awaited<ReturnType<typeof createBreakdownCopyJob>> | undefined;
      for (let attempt = 0; attempt < 3 && !response; attempt++) {
        try {
          response = await createBreakdownCopyJob(createJobPayload);
        } catch (error) {
          if (attempt === 2) throw error;
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        }
      }

      let job = response!.data;
      let pollingFailures = 0;
      while (job.status === 'PENDING' || job.status === 'RUNNING') {
        setCopyProgress(job.progress);
        setCopyBreakdownStatusMessage(
          job.stage === 'PREPARING'
            ? t('home.copyBreakdownStages.PREPARING')
            : `${t(`home.copyBreakdownStages.${job.stage}`)} (${job.completedItems}/${job.totalItems})`,
        );
        await new Promise(resolve => setTimeout(resolve, 1000));
        try {
          job = (await getBreakdownCopyJob(job.jobID)).data;
          pollingFailures = 0;
        } catch (error) {
          pollingFailures += 1;
          if (pollingFailures >= 10) throw error;
          setCopyBreakdownStatusMessage(t('home.copyBreakdownReconnecting'));
          const retryDelay = Math.min(5000, pollingFailures * 1000);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }

      if (job.status === 'FAILED') {
        throw new Error(job.errorMessage || t('home.copyBreakdownJobFailed'));
      }

      const newBreakdownID = job.targetBreakdownID;

      if (!newBreakdownID) {
        throw new Error('复制分场失败：未返回 breakdownID');
      }

      const newBreakdown = (await getBreakdownById(newBreakdownID)).data;

      setCopyProgress(100);

      appendSceneToCurrentFilm(newBreakdown as BreakdownInfo);

      // 重置状态
      setCopyingBreakdownId(null);
      setCopyBreakdownStatusMessage('');
      setCopyProgress(0);

      // 复制完成后不自动打开新分场
    } catch (error) {
      console.error('复制分场失败:', error);
      alert(t('home.duplicateBreakdownFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
      setCopyingBreakdownId(null);
      setCopyBreakdownStatusMessage('');
      setCopyProgress(0);
    }
  }, [t, selectedFilmID, appendSceneToCurrentFilm]);

  // ========== 复制预算相关函数 ==========
  
  /**
   * 复制预算
   */
  const handleCopyBudget = useCallback(async (budgetID: string) => {
    try {
      setCopyingBudgetId(budgetID);

      // 获取当前用户ID和组织ID
      const currentUserID = getCurrentUserID();
      const currentUserOrgID = getCurrentUserOrgID();
      if (!currentUserID) {
        throw new Error('无法获取当前用户信息');
      }
      if (!currentUserOrgID) {
        throw new Error('无法获取用户组织信息');
      }

      // 获取原始预算信息以生成新名称
      const budgetInfoResponse = await getBudgetById(budgetID);
      const budgetInfo = budgetInfoResponse.data;
      const originalName = budgetInfo.budgetName || '';

      // 生成唯一的预算名称（后缀随界面语言变化）
      let suffixNumber = 1;
      let newBudgetName = `${originalName}${t('common.copyNameSuffix')}`;

      // 检查当前影片项目下是否已存在同名预算
      if (selectedFilmID) {
        const existingBudgetsResponse = await getBudgetsByFilmId(selectedFilmID);
        const existingBudgets = existingBudgetsResponse.data || [];
        const existingNames = new Set(existingBudgets.map((b: BudgetResponse) => b.budgetName));

        while (existingNames.has(newBudgetName)) {
          suffixNumber++;
          newBudgetName = `${originalName}${t('common.copyNameSuffixNumbered', { n: suffixNumber })}`;
        }
      }

      // 调用后端复制接口
      const response = await copyBudget({
        sourceBudgetID: budgetID,
        targetBudgetName: newBudgetName,
      });

      const newBudget = response.data;
      const newBudgetID = newBudget.budgetID;

      if (!newBudgetID) {
        throw new Error('复制预算失败：未返回 budgetID');
      }

      // 重置状态
      setCopyingBudgetId(null);

      // 将 Budget 转换为 BudgetResponse 格式
      const newBudgetResponse: BudgetResponse = {
        budgetID: newBudget.budgetID,
        budgetName: newBudget.budgetName ?? '',
        budgetParentID: newBudget.budgetParentID ?? '',
        budgetFilmID: newBudget.budgetFilmID ?? '',
        budgetLevel: newBudget.budgetLevel ?? '总预算',
        budgetOwnerID: newBudget.budgetOwnerID ?? '',
        budgetAssigneeID: newBudget.budgetAssigneeID ?? '',
        budgetPartnersID: newBudget.budgetPartnersID ?? '',
        budgetCreatorID: newBudget.budgetCreatorID ?? '',
        budgetCreateTime: newBudget.budgetCreateTime ? new Date(newBudget.budgetCreateTime) : new Date(),
        budgetUpdateTime: newBudget.budgetUpdateTime ? new Date(newBudget.budgetUpdateTime) : new Date(),
        budgetDataStatus: newBudget.budgetDataStatus ?? '',
        budgetStatus: newBudget.budgetStatus ?? '预算中',
        budgetTotalAmount: newBudget.budgetTotalAmount ?? 0,
        budgetOverview: newBudget.budgetOverview ?? '',
        budgetStorageMode: newBudget.budgetStorageMode ?? '在线',
        budgetStorageFolderAddress: newBudget.budgetStorageFolderAddress ?? '',
        budgetUsedAmount: newBudget.budgetUsedAmount ?? 0,
        budgetUnusedAmount: newBudget.budgetUnusedAmount ?? 0,
        budgetExceedingAmount: newBudget.budgetExceedingAmount ?? 0,
        budgetCurrency: newBudget.budgetCurrency,
      };

      appendBudgetToCurrentFilm(newBudgetResponse);

      // 打开新复制的预算
      handleBudgetClick(newBudgetResponse);
    } catch (error) {
      console.error('复制预算失败:', error);
      alert(t('home.duplicateBudgetFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
      setCopyingBudgetId(null);
    }
  }, [t, handleBudgetClick, selectedFilmID, appendBudgetToCurrentFilm]);

  // ========== 格式化函数 ==========
  const formatCardDate = (dateString: string | Date | undefined) => {
    if (!dateString) return '';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}  ${hours}:${minutes}`;
  };

  const formatCardDateShort = (dateString: string | Date | undefined) => {
    if (!dateString) return '';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  // 点击外部关闭删除确认框和复制确认框
  useEffect(() => {
    if (showDeleteBudgetConfirm || showDeleteBreakdownConfirm || editingOverviewId) {
      const handleDocumentClick = (e: MouseEvent) => {
        const target = e.target as Element;
        if (showDeleteBudgetConfirm && !target.closest('.delete-confirm-card')) {
          setShowDeleteBudgetConfirm(null);
        }
        if (showDeleteBreakdownConfirm && !target.closest('.delete-confirm-card') && !target.closest('.home-card-action-btn')) {
          setShowDeleteBreakdownConfirm(null);
        }
        if (editingOverviewId && !target.closest('.home-card-info')) {
          setEditingOverviewId(null);
          setEditingOverview('');
          setOverviewError(null);
        }
      };
      
      document.addEventListener('click', handleDocumentClick);
      return () => {
        document.removeEventListener('click', handleDocumentClick);
      };
    }
  }, [showDeleteBudgetConfirm, showDeleteBreakdownConfirm, editingOverviewId]);
  
  return (
    <div className="home-container">
      <div className="main-content">
        {/* 新的 main-left: 影片项目列表 */}
        <div className='main-left'>
          {/* 用户资料行 */}
          {userInfo && (
            <div className='main-left-user-profile'>
              <div className="user-profile-row">
                <div className="user-profile-left">
                  <div 
                    className="user-avatar-initial" 
                    onClick={handleAvatarClick}
                    style={{ cursor: canEditCurrentFilm ? 'pointer' : 'default' }}
                  >
                    {getUserInitial()}
                  </div>
                  <div className="user-name">
                    {userInfo.userNickName || userInfo.userName}
                    <span className="user-type-badge">{getUserTypeText()}</span>
                  </div>
                </div>
                <div className="user-profile-actions">
                  {showUserDetail ? (
                    <>
                      <button className="profile-action-btn" onClick={() => {/* 占位 */}}>
                        {t('home.editProfile')}
                      </button>
                      <button className="profile-action-btn" onClick={handleCloseUserDetail}>
                        {t('home.close')}
                      </button>
                    </>
                  ) : (
                    <button className="profile-action-btn" onClick={handleLogout}>
                      <ExitIcon className="profile-action-icon" />
                      <span>{t('home.logout')}</span>
                    </button>
                  )}
                </div>
              </div>
              {/* 用户详情容器 */}
              {showUserDetail && (
                <div className="user-profile-detail">
                  <div className="info-item">
                    <span className="info-label">{t('home.username')}</span>
                    <span className="info-value">{userInfo.userName || '-'}</span>
                    <button className="info-action-btn" onClick={() => {/* 占位 */}}>
                      {t('home.changePassword')}
                    </button>
                  </div>

                  <div className="info-item">
                    <span className="info-label">{t('home.boundEmail')}</span>
                    <span className="info-value">{userInfo.userEmail || '-'}</span>
                    <button className="info-action-btn" onClick={() => {/* 占位 */}}>
                      {t('home.change')}
                    </button>
                  </div>

                  <div className="info-item">
                    <span className="info-label">{t('home.boundPhone')}</span>
                    <span className="info-value">{userInfo.userPhone || '-'}</span>
                    <button className="info-action-btn" onClick={() => {/* 占位 */}}>
                      {t('home.change')}
                    </button>
                  </div>

                  <div className="info-item">
                    <span className="info-label">{t('home.subscriptionVersion')}</span>
                    <span className="info-value">{getSubscriptionVersion()}</span>
                    <button className="info-action-btn" onClick={() => {/* 占位 */}}>
                      {t('home.upgradeSubscription')}
                    </button>
                  </div>

                  <div className="info-item">
                    <span className="info-label">{t('home.expirationDate')}</span>
                    <span className="info-value">
                      {formatDate(userInfo.userSubscriptionExpirationDate)}
                    </span>
                    <button className="info-action-btn" onClick={() => {/* 占位 */}}>
                      {t('home.renew')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className='main-left-films-header'>
            <div className='main-left-films-header-top'>
              <span className='main-left-films-title'>{t('home.allFilms')}</span>
              <button 
                className='main-left-films-add-btn'
                onClick={() => setIsCreateFilmModalOpen(true)}
                title={t('home.createFilm')}
              >
                <AddIcon className="main-left-films-add-icon" />
              </button>
            </div>
            {/* 影片项目筛选标签 */}
            <div className="film-filter-tabs">
              <button
                className={`film-filter-tab ${filmFilterTab === 'all' ? 'active' : ''}`}
                onClick={() => setFilmFilterTab('all')}
              >
                <span>{t('home.filmFilterAll')}</span>
              </button>
              <button
                className={`film-filter-tab ${filmFilterTab === 'mine' ? 'active' : ''}`}
                onClick={() => setFilmFilterTab('mine')}
              >
                <span>{t('home.filmFilterMine')}</span>
              </button>
              <button
                className={`film-filter-tab ${filmFilterTab === 'participated' ? 'active' : ''}`}
                onClick={() => setFilmFilterTab('participated')}
              >
                <span>{t('home.filmFilterParticipated')}</span>
              </button>
            </div>
          </div>
          <div className='main-left-films-content'>
            {filmsLoading ? (
              <div className='films-loading'>{t('home.filmsLoading')}</div>
            ) : filteredFilms.length === 0 ? (
              <div className='films-empty'>{t('home.filmsEmpty')}</div>
            ) : (
              <div className='films-list'>
                {filteredFilms.map((film: Film) => {
                  const isActive = selectedFilmID === film.filmID;
                  // 根据项目类型选择图标
                  const ProjectIcon = film.filmProjectType === '预算项目' ? BudgetIcon : FilmIcon;
                  return (
                    <div
                      key={film.filmID}
                      className={`film-list-item ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        void handleFilmSelect(film);
                      }}
                    >
                      <div className='film-list-item-icon'>
                        <ProjectIcon className="film-icon" />
                      </div>
                      <div className='film-list-item-text'>
                        <span className='film-list-item-name'>{film.filmName || t('film.untitledFilm')}</span>
                        {filmFilterTab === 'all' && film.filmOwnerID === getCurrentUserID() && (
                          <span className='film-list-item-owner-tag'>{t('home.filmFilterMine')}</span>
                        )}
                      </div>
                      {film.filmProjectType && (
                        <div className='film-list-item-type-tag'>
                          {film.filmProjectType === '预算项目' ? t('home.budgetProject') : t('home.breakdownProject')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* 更多设置容器 */}
          <div className='main-left-more'>
            <div 
              className='main-left-more-title'
              onClick={handleMoreSettingsToggle}
            >
              <div className='main-left-more-title-left'>
                <SettingIcon className='main-left-more-icon' />
                <span className='main-left-more-title-text'>{t('home.moreSettings')}</span>
              </div>
              {showMoreSettings && (
                <button 
                  className='main-left-more-close-btn'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseMoreSettings();
                  }}
                >
                  {t('home.close')}
                </button>
              )}
            </div>
            {showMoreSettings && (
              <div className='main-left-more-content'>
                {/* Tab切换 */}
                <div className='main-left-more-content-tabs'>
                  <button
                    className={`more-tab ${moreSettingsTab === 'setting' ? 'active' : ''}`}
                    onClick={() => setMoreSettingsTab('setting')}
                  >
                    {t('home.appSettings')}
                  </button>
                  <button
                    className={`more-tab ${moreSettingsTab === 'about' ? 'active' : ''}`}
                    onClick={() => setMoreSettingsTab('about')}
                  >
                    {t('home.aboutLineup')}
                  </button>
                </div>
                {/* 应用设置内容 */}
                {moreSettingsTab === 'setting' && (
                  <div className='main-left-more-content-setting'>
                    <div className="info-item">
                      <span className="info-label">{t('home.uiLanguage')}</span>
                      <div 
                        ref={languageButtonRef}
                        className="info-value-with-icon" 
                        onClick={() => setLanguageMenuVisible(!languageMenuVisible)}
                      >
                        <span className="language-icon-char">
                          {getLanguageChar(currentLanguage)}
                        </span>
                        <span>{getLanguageName(currentLanguage)}</span>
                      </div>
                      <DropdownMenu
                        items={languageOptions}
                        visible={languageMenuVisible}
                        onClose={() => setLanguageMenuVisible(false)}
                        onSelect={handleLanguageSelect}
                        position={languageMenuPosition}
                        useFixedPosition={true}
                        selectedValue={currentLanguage}
                        width={languageButtonRef.current?.offsetWidth}
                      />
                    </div>

                    <div className="info-item">
                      <span className="info-label">{t('home.preferredCurrency')}</span>
                      <div 
                        ref={currencyButtonRef}
                        className="info-value-with-icon" 
                        onClick={() => setCurrencyMenuVisible(!currencyMenuVisible)}
                      >
                        <span className="currency-icon-char">
                          {getCurrencySymbol(selectedCurrency)}
                        </span>
                        <span>{getCurrencyDisplayName(selectedCurrency)}</span>
                      </div>
                      <DropdownMenu
                        items={currencyOptions}
                        visible={currencyMenuVisible}
                        onClose={() => setCurrencyMenuVisible(false)}
                        onSelect={handleCurrencySelect}
                        position={currencyMenuPosition}
                        useFixedPosition={true}
                        selectedValue={selectedCurrency}
                        width={currencyButtonRef.current?.offsetWidth}
                      />
                    </div>

                    <div className="info-item">
                      <span className="info-label">{t('home.uiDensity')}</span>
                      <div className="info-value-radio-group">
                        <label className="radio-option">
                          <input 
                            type="radio" 
                            name="ui-density" 
                            value="compact" 
                            checked={uiDensity === 'compact'}
                            onChange={() => handleUiDensityChange('compact')}
                          />
                          <span>{t('home.compact')}</span>
                        </label>
                        <label className="radio-option">
                          <input 
                            type="radio" 
                            name="ui-density" 
                            value="loose"
                            checked={uiDensity === 'loose'}
                            onChange={() => handleUiDensityChange('loose')}
                          />
                          <span>{t('home.loose')}</span>
                        </label>
                      </div>
                    </div>

                    <div className="info-item">
                      <span className="info-label">{t('home.uiStyle')}</span>
                      <div className="theme-color-buttons">
                        {(['dark', 'blue', 'green', 'orange'] as const).map((themeKey) => (
                          <button
                            key={themeKey}
                            type="button"
                            className={`theme-color-btn ${themeKey === theme ? 'selected' : ''}`}
                            style={{ '--theme-color': THEME_COLORS[themeKey] } as React.CSSProperties}
                            onClick={() => handleThemeSelect(themeKey)}
                            title={t(`home.theme.${themeKey}`)}
                            aria-label={t(`home.theme.${themeKey}`)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {/* 关于LINEUP内容 */}
                {moreSettingsTab === 'about' && (
                  <div className='main-left-more-content-about'>
                    <button type="button" className="about-link" onClick={() => { /* 占位 */ }}>
                      {t('home.changelog')}
                    </button>
                    <button type="button" className="about-link" onClick={() => { /* 占位 */ }}>
                      {t('home.userAgreement')}
                    </button>
                    <button type="button" className="about-link" onClick={() => { /* 占位 */ }}>
                      {t('home.privacyPolicy')}
                    </button>
                    <button type="button" className="about-link" onClick={() => { /* 占位 */ }}>
                      {t('home.softwareUpdate')}
                    </button>
                    <button type="button" className="about-link" onClick={() => { /* 占位 */ }}>
                      {t('home.helpAndSupport')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* 原来的 main-left 改为 main-middle */}
        <div className='main-middle'>
          {/* 影片信息容器 - 始终显示 */}
          <div className='main-middle-film'>
            <div className='main-middle-film-header'>
              <div className='main-middle-film-header-name'>
                <span className='film-label'>{t('home.filmProject')}</span>
                {canEditCurrentFilm && currentFilm && editingFilmField === 'filmName' ? (
                  <input
                    type="text"
                    className="film-name-input"
                    value={editingFieldValue}
                    onChange={(e) => setEditingFieldValue(e.target.value)}
                    onBlur={() => handleFieldSave('filmName')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        handleFieldCancel();
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span 
                    className="film-name-display" 
                    onClick={() => handleFieldClick('filmName')}
                    style={{ cursor: canEditCurrentFilm ? 'pointer' : 'default' }}
                  >
                    {currentFilm?.filmName || t('film.untitledFilm')}
                  </span>
                )}
              </div>
              {currentFilm && (
                <div className='main-middle-film-header-buttons'>
                  {isEditingFilm ? (
                    <>
                      <button className="film-action-btn" onClick={handleSaveFilm} title={t('home.save')}>
                        <span>{t('home.save')}</span>
                      </button>
                      <button className="film-action-btn" onClick={handleCancelEdit} title={t('home.cancel')}>
                        <span>{t('home.cancel')}</span>
                      </button>
                    </>
                  ) : (
                    <>
                      {canManageCurrentFilmMembers &&
                        renderFilmUserActionButton('share', 'home.shareFilm', ShareIcon)}
                      <button className="film-action-btn" type="button" title={t('home.downloadAsLocalFile')}>
                        <DownloadIcon className="film-action-icon" />
                        <span>{t('home.downloadAsLocalFile')}</span>
                      </button>
                      {canDeleteCurrentFilm && (
                        <button className="film-action-btn" onClick={handleDeleteFilm} title={t('home.delete')}>
                          <DeleteIcon className="film-action-icon" />
                          <span>{t('home.delete')}</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className='main-middle-film-info'>
              <div className='film-info-field'>
                <span className='film-info-label'>{t('film.executiveProducer')}</span>
                {canEditCurrentFilm && currentFilm && editingFilmField === 'filmProducer' ? (
                  <input
                    type="text"
                    className="film-info-input"
                    value={editingFieldValue}
                    onChange={(e) => setEditingFieldValue(e.target.value)}
                    onBlur={() => handleFieldSave('filmProducer')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        handleFieldCancel();
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span 
                    className="film-info-value" 
                    onClick={() => handleFieldClick('filmProducer')}
                    style={{ cursor: canEditCurrentFilm ? 'pointer' : 'default' }}
                  >
                    {currentFilm?.filmProducer || '-'}
                  </span>
                )}
              </div>
              <div className='film-info-field'>
                <span className='film-info-label'>{t('film.director')}</span>
                {canEditCurrentFilm && currentFilm && editingFilmField === 'filmDirector' ? (
                  <input
                    type="text"
                    className="film-info-input"
                    value={editingFieldValue}
                    onChange={(e) => setEditingFieldValue(e.target.value)}
                    onBlur={() => handleFieldSave('filmDirector')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        handleFieldCancel();
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span 
                    className="film-info-value" 
                    onClick={() => handleFieldClick('filmDirector')}
                    style={{ cursor: canEditCurrentFilm ? 'pointer' : 'default' }}
                  >
                    {currentFilm?.filmDirector || '-'}
                  </span>
                )}
              </div>
              <div className='film-info-field'>
                <span className='film-info-label'>{t('film.screenwriter')}</span>
                {canEditCurrentFilm && currentFilm && editingFilmField === 'filmScreenwriter' ? (
                  <input
                    type="text"
                    className="film-info-input"
                    value={editingFieldValue}
                    onChange={(e) => setEditingFieldValue(e.target.value)}
                    onBlur={() => handleFieldSave('filmScreenwriter')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        handleFieldCancel();
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span 
                    className="film-info-value" 
                    onClick={() => handleFieldClick('filmScreenwriter')}
                    style={{ cursor: canEditCurrentFilm ? 'pointer' : 'default' }}
                  >
                    {currentFilm?.filmScreenwriter || '-'}
                  </span>
                )}
              </div>
              <div className='film-info-field'>
                <span className='film-info-label'>{t('film.filmTypeLabel')}</span>
                {canEditCurrentFilm && currentFilm && editingFilmField === 'filmType' ? (
                  <>
                    <button
                      ref={filmTypeButtonRef}
                      type="button"
                      className="film-info-select-button"
                      onClick={() => setFilmTypeMenuVisible(!filmTypeMenuVisible)}
                    >
                      {(editingFieldValue && filmTypeKeyMap[editingFieldValue])
                        ? t(`film.filmTypes.${filmTypeKeyMap[editingFieldValue]}`)
                        : (editingFieldValue || '-')}
                    </button>
                    <DropdownMenu
                      items={filmTypeItems}
                      visible={filmTypeMenuVisible}
                      onClose={() => {
                        setFilmTypeMenuVisible(false);
                        handleFieldCancel();
                      }}
                      onSelect={(item) => {
                        const selectedValue = item.id as string;
                        setFilmTypeMenuVisible(false);
                        handleFieldSave('filmType', selectedValue);
                      }}
                      position={filmTypeMenuPosition}
                      width={filmTypeMenuWidth}
                      useFixedPosition={true}
                      selectedValue={editingFieldValue || ''}
                    />
                  </>
                ) : (
                  <span 
                    className="film-info-value" 
                    onClick={() => handleFieldClick('filmType')}
                    style={{ cursor: 'pointer' }}
                  >
                    {(currentFilm?.filmType && filmTypeKeyMap[currentFilm.filmType])
                      ? t(`film.filmTypes.${filmTypeKeyMap[currentFilm.filmType]}`)
                      : (currentFilm?.filmType || '-')}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className='main-middle-header'>
            <div className='main-middle-header-left'>
              {/* 筛选标签按钮容器 */}
              {activeTab && (
                <div className="film-tabs">
                  <button
                    className={`film-tab ${filterTab === 'all' ? 'active' : ''}`}
                    onClick={() => setFilterTab('all')}
                  >
                    <span>{activeTab === 'breakdown' ? t('home.breakdownList') : t('home.allBudget')}</span>
                  </button>
                </div>
              )}
            </div>
            <div className='main-middle-header-right'>
              {/* 排序容器 */}
              {activeTab === 'breakdown' && showBreakdownTab && (
                <>
                  <div className="home-breakdown-sort">
                    <span className="home-breakdown-sort-label">{t('home.sortBy')}</span>
                    <button
                      ref={breakdownSortButtonRef}
                      className="home-breakdown-sort-field-btn"
                      onClick={() => setBreakdownSortMenuVisible(!breakdownSortMenuVisible)}
                    >
                      {getBreakdownSortFieldLabel()}
                    </button>
                    <button
                      className="home-breakdown-sort-direction-btn"
                      onClick={() => setBreakdownSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                      title={breakdownSortDirection === 'asc' ? t('home.switchToDescending') : t('home.switchToAscending')}
                    >
                      {getSortDirectionLabel(breakdownSortDirection)}
                    </button>
                    <span className="home-breakdown-sort-label">{t('home.sort')}</span>
                  </div>
                  <DropdownMenu
                    items={breakdownSortOptions}
                    visible={breakdownSortMenuVisible}
                    onClose={() => setBreakdownSortMenuVisible(false)}
                    onSelect={handleBreakdownSortFieldSelect}
                    position={breakdownSortButtonPosition}
                    width={breakdownSortButtonWidth}
                    useFixedPosition={true}
                    selectedValue={breakdownSortKey}
                  />
                </>
              )}
              {activeTab === 'budget' && showBudgetTab && (
                <>
                  <div className="home-breakdown-sort">
                    <span className="home-breakdown-sort-label">{t('home.sortBy')}</span>
                    <button
                      ref={budgetSortButtonRef}
                      className="home-breakdown-sort-field-btn"
                      onClick={() => setBudgetSortMenuVisible(!budgetSortMenuVisible)}
                    >
                      {getBudgetSortFieldLabel()}
                    </button>
                    <button
                      className="home-breakdown-sort-direction-btn"
                      onClick={() => setBudgetSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                      title={budgetSortDirection === 'asc' ? t('home.switchToDescending') : t('home.switchToAscending')}
                    >
                      {getSortDirectionLabel(budgetSortDirection)}
                    </button>
                    <span className="home-breakdown-sort-label">{t('home.sort')}</span>
                  </div>
                  <DropdownMenu
                    items={budgetSortOptions}
                    visible={budgetSortMenuVisible}
                    onClose={() => setBudgetSortMenuVisible(false)}
                    onSelect={handleBudgetSortFieldSelect}
                    position={budgetSortButtonPosition}
                    width={budgetSortButtonWidth}
                    useFixedPosition={true}
                    selectedValue={budgetSortKey}
                  />
                </>
              )}
              {/* 创建按钮 */}
              {activeTab === 'breakdown' && showBreakdownTab && (
                <button 
                  className="createBreakdown-button"
                  onClick={() => createBreakdownHandlerRef.current?.()}
                >
                  <img src={addIcon} className="add-icon" alt="add" />
                  {t('breakdownList.createNewBreakdown')}
                </button>
              )}
              {activeTab === 'budget' && showBudgetTab && (
                <button 
                  className="createBreakdown-button"
                  onClick={async () => {
                    if (createBudgetHandlerRef.current) {
                      await createBudgetHandlerRef.current();
                    }
                  }}
                >
                  <img src={addIcon} className="add-icon" alt="add" />
                  {t('budget.createNewBudget')}
                </button>
              )}
            </div>
          </div>
          <div className='main-middle-content'>
            {activeTab === 'breakdown' && showBreakdownTab && (
              <div className="home-cards">
                {breakdownLoading ? (
                  <div style={{ gridColumn: '1 / -1', padding: '20px', textAlign: 'center' }}>{t('common.loading')}</div>
                ) : breakdownError ? (
                  <div style={{ gridColumn: '1 / -1', padding: '20px', textAlign: 'center', color: 'var(--color-red)' }}>{breakdownError}</div>
                ) : sortedBreakdown.length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', padding: '20px', textAlign: 'center', color: 'var(--color-gray2)' }}>{t('breakdownList.noBreakdownData')}</div>
                ) : (
                  sortedBreakdown.map(bd => {
                    const capabilities = getBreakdownCardCapabilities(bd);
                    return (
                    <div 
                      key={bd.breakdownID} 
                      className="home-card"
                      onClick={() => handleSceneClick(bd.breakdownID, bd.breakdownName)}
                    >
                      {copyingBreakdownId === bd.breakdownID && (
                        <div className="home-card-progress-bar">
                          <div 
                            className="home-card-progress-bar-fill"
                            style={{ width: `${copyProgress}%` }}
                          />
                        </div>
                      )}
                      <div className="home-card-left">
                        <div className="home-card-title-label">{t('home.breakdownVersion')}</div>
                        {editingBreakdownId === bd.breakdownID ? (
                          <>
                            <input
                              ref={breakdownNameInputRef}
                              type="text"
                              value={editingBreakdownName}
                              onChange={(e) => {
                                setEditingBreakdownName(e.target.value);
                                const isDuplicate = breakdown.some(s => 
                                  s.breakdownID !== bd.breakdownID && 
                                  s.breakdownName.trim() === e.target.value.trim()
                                );
                                if (e.target.value.trim() && isDuplicate) {
                                  setBreakdownNameError('已有相同版本的分场，请重新命名');
                                } else if (!e.target.value.trim()) {
                                  setBreakdownNameError(t('home.breakdownNameRequired'));
                                } else {
                                  setBreakdownNameError(null);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={`home-card-name-input ${breakdownNameError ? 'error' : ''}`}
                            />
                            {breakdownNameError && <div className="home-card-name-error">{breakdownNameError}</div>}
                          </>
                        ) : (
                          <div className="home-card-name">{bd.breakdownName}</div>
                        )}
                        {copyingBreakdownId === bd.breakdownID ? (
                          <div className="home-card-info home-card-copy-status">
                            <span>{t('home.copyBreakdownLongRunningHint')}</span>
                            <span>{copyBreakdownStatusMessage}</span>
                          </div>
                        ) : (
                        <div className="home-card-info">
                          <span>
                            {t('home.owner')}{t('common.labelSeparator')}{bd.ownerInfo?.userNickname || '-'}
                            {filterTab === 'all' && bd.breakdownOwnerID === getCurrentUserID() && (
                              <span className="home-card-owner-mine-badge">{t('home.mineBadge')}</span>
                            )}
                          </span>
                          <span>{t('home.lastUpdate')}{t('common.labelSeparator')}{formatCardDate(bd.breakdownUpdateTime)}</span>
                          <span>
                            {t('home.scriptVersion')}{t('common.labelSeparator')}
                            {editingOverviewId === bd.breakdownID ? (
                              <input
                                ref={overviewInputRef}
                                type="text"
                                value={editingOverview}
                                onChange={(e) => {
                                  setEditingOverview(e.target.value);
                                  setOverviewError(null);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className={`home-card-info-input ${overviewError ? 'error' : ''}`}
                                style={{ marginLeft: '2px', width: 'calc(100% - 80px)' }}
                              />
                            ) : (
                              bd.breakdownScriptOverview || '--'
                            )}
                          </span>
                        </div>
                        )}
                      </div>
                      <div className="home-card-right">
                        <div className="home-card-icon">
                          <CardBreakdownIcon />
                        </div>
                        <div className="home-card-actions">
                          {capabilities.canEdit && (editingBreakdownId === bd.breakdownID ? (
                            <button
                              className="home-card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBreakdownSave(bd.breakdownID);
                              }}
                              title={breakdownNameError || overviewError ? t('home.cannotSave') : t('home.saveBreakdown')}
                              disabled={!!breakdownNameError || !!overviewError}
                            >
                              <img 
                                src={breakdownNameError || overviewError ? saveNoIcon : saveIcon} 
                                alt={t('home.save')} 
                                className={breakdownNameError || overviewError ? 'disabled' : ''}
                              />
                            </button>
                          ) : (
                            <button
                              className="home-card-action-btn"
                              onClick={(e) => handleBreakdownEditClick(e, bd.breakdownID, bd.breakdownName, bd.breakdownScriptOverview)}
                            >
                              <EditIcon className="edit-icon" aria-label={t('home.edit')} />
                            </button>
                          ))}
                          {capabilities.canCopy && (copyingBreakdownId === bd.breakdownID ? (
                            <button
                              className="home-card-action-btn"
                              disabled
                              title={t('home.duplicating')}
                            >
                              <WaitIcon className="home-card-copy-icon copying" aria-label={t('home.duplicating')} />
                            </button>
                          ) : (
                            <button
                              className="home-card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyBreakdown(bd.breakdownID);
                              }}
                              title={t('home.duplicateBreakdown')}
                            >
                              <CopyIcon className="home-card-copy-icon" aria-label={t('home.duplicateBreakdown')} />
                            </button>
                          ))}
                          {capabilities.canDelete && <div style={{ position: 'relative' }}>
                            {showDeleteBreakdownConfirm === bd.breakdownID && (
                              <div className="delete-confirm-card">
                                <span>{t('home.confirmDelete')}</span>
                                <div className="delete-confirm-card-buttons">
                                  <button 
                                    className="delete-confirm-card-yes"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleBreakdownDelete(bd.breakdownID);
                                    }}
                                  >
                                    {t('home.yes')}
                                  </button>
                                  <button 
                                    className="delete-confirm-card-no"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowDeleteBreakdownConfirm(null);
                                    }}
                                  >
                                    {t('home.no')}
                                  </button>
                                </div>
                              </div>
                            )}
                            <button
                              className="home-card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (showDeleteBreakdownConfirm === bd.breakdownID) {
                                  setShowDeleteBreakdownConfirm(null);
                                } else {
                                  setShowDeleteBreakdownConfirm(bd.breakdownID);
                                }
                              }}
                            >
                              <DeleteIcon className="delete-icon" aria-label={t('home.delete')} />
                            </button>
                          </div>}
                        </div>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            )}
            {activeTab === 'budget' && showBudgetTab && (
              <div className="home-cards">
                {budgetsLoading ? (
                  <div style={{ gridColumn: '1 / -1', padding: '20px', textAlign: 'center' }}>{t('budget.loading')}</div>
                ) : budgetsError ? (
                  <div style={{ gridColumn: '1 / -1', padding: '20px', textAlign: 'center', color: 'var(--color-red)' }}>{budgetsError}</div>
                ) : filteredBudgets.length === 0 ? (
                  <div style={{ gridColumn: '1 / -1', padding: '20px', textAlign: 'center', color: 'var(--color-gray2)' }}>{t('home.noBudget')}</div>
                ) : (
                  filteredBudgets.slice(0, 24).map(budget => (
                    <div 
                      key={budget.budgetID} 
                      className="home-card"
                      onClick={() => handleBudgetClick(budget)}
                    >
                      <div className="home-card-left">
                        <div className="home-card-title-label">{t('home.budgetVersion')}</div>
                        {editingBudgetId === budget.budgetID ? (
                          <>
                            <input
                              ref={budgetNameInputRef}
                              type="text"
                              value={editingBudgetName}
                              onChange={(e) => {
                                setEditingBudgetName(e.target.value);
                                const isDuplicate = budgets.some(b => 
                                  b.budgetID !== budget.budgetID && 
                                  b.budgetName.trim() === e.target.value.trim()
                                );
                                if (e.target.value.trim() && isDuplicate) {
                                  setBudgetNameError(t('home.budgetDuplicateError'));
                                } else if (!e.target.value.trim()) {
                                  setBudgetNameError(t('home.budgetNameRequired'));
                                } else {
                                  setBudgetNameError(null);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className={`home-card-name-input ${budgetNameError ? 'error' : ''}`}
                            />
                            {budgetNameError && <div className="home-card-name-error">{budgetNameError}</div>}
                          </>
                        ) : (
                          <div className="home-card-name">{budget.budgetName || t('budget.untitledBudget')}</div>
                        )}
                        <div className="home-card-amount">
                          {formatCurrencyAmount(
                            Number(budget.budgetTotalAmount || 0),
                            budget.budgetCurrency || 'RMB',
                            true
                          )}
                        </div>
                        <div className="home-card-info">
                          <span>{t('home.owner')}{t('common.labelSeparator')}{budget.ownerInfo?.userNickname || budget.budgetOwnerID || '-'}</span>
                          <span>{t('home.lastUpdate')}{t('common.labelSeparator')}{formatCardDateShort(budget.budgetUpdateTime)}</span>
                        </div>
                      </div>
                      <div className="home-card-right">
                        <div className="home-card-icon">
                          <CardBudgetIcon />
                        </div>
                        <div className="home-card-actions">
                          {editingBudgetId === budget.budgetID ? (
                            <button
                              className="home-card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBudgetSave(budget.budgetID);
                              }}
                              title={budgetNameError ? t('home.cannotSave') : t('home.saveBudget')}
                              disabled={!!budgetNameError}
                            >
                              <img 
                                src={budgetNameError ? saveNoIcon : saveIcon} 
                                alt={t('home.save')} 
                                className={budgetNameError ? 'disabled' : ''}
                              />
                            </button>
                          ) : (
                            <button
                              className="home-card-action-btn"
                              onClick={(e) => handleBudgetEditClick(e, budget.budgetID, budget.budgetName)}
                            >
                              <EditIcon className="edit-icon" aria-label={t('home.edit')} />
                            </button>
                          )}
                          {copyingBudgetId === budget.budgetID ? (
                            <button
                              className="home-card-action-btn"
                              disabled
                              title={t('home.duplicating')}
                            >
                              <WaitIcon className="home-card-copy-icon copying" aria-label={t('home.duplicating')} />
                            </button>
                          ) : (
                            <button
                              className="home-card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyBudget(budget.budgetID);
                              }}
                              title={t('home.duplicateBudget')}
                            >
                              <CopyIcon className="home-card-copy-icon" aria-label={t('home.duplicate')} />
                            </button>
                          )}
                          {showDeleteBudgetConfirm === budget.budgetID ? (
                            <div className="delete-confirm-card">
                              <span>{t('home.confirmDelete')}</span>
                              <div className="delete-confirm-card-buttons">
                                <button 
                                  className="delete-confirm-card-yes"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBudgetDelete(budget.budgetID);
                                  }}
                                >
                                  {t('home.yes')}
                                </button>
                                <button 
                                  className="delete-confirm-card-no"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDeleteBudgetConfirm(null);
                                  }}
                                >
                                  {t('home.no')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              className="home-card-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowDeleteBudgetConfirm(budget.budgetID);
                              }}
                            >
                              <DeleteIcon className="delete-icon" aria-label={t('home.delete')} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            {activeTab === null && !showBreakdownTab && !showBudgetTab && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-gray2)' }}>
                您还没有订阅任何服务
              </div>
            )}
          </div>
        </div>
      </div>
      {/* 创建影片模态框 */}
      <CreateFilmModal
        isOpen={isCreateFilmModalOpen}
        onClose={() => setIsCreateFilmModalOpen(false)}
        onSubmit={handleCreateFilm}
      />

      {/* 创建分场对话框 */}
      <FormDialog
        visible={showCreateBreakdownDialog}
        title={t('breakdownList.createDialogTitle')}
        formItems={[
          {
            type: 'input',
            id: 'breakdownName',
            label: t('breakdownList.breakdownNameLabel'),
            required: true
          },
          {
            type: 'input',
            id: 'breakdownVersion',
            label: t('breakdownList.breakdownVersionLabel'),
            required: true
          }
        ]}
        message={createBreakdownFormMessage}
        actions={[
          {
            label: t('common.cancel'),
            type: 'secondary',
            onClick: () => {
              handleCloseCreateBreakdownDialog();
            }
          },
          {
            label: t('breakdownList.createAndOpenButton'),
            type: 'primary',
            onClick: (formData: Record<string, string | number>) => {
              if (!formData.breakdownName || String(formData.breakdownName).trim() === '') {
                setCreateBreakdownFormMessage(t('breakdownList.breakdownNameRequired'));
                return false;
              }
              if (!formData.breakdownVersion || String(formData.breakdownVersion).trim() === '') {
                setCreateBreakdownFormMessage(t('breakdownList.breakdownVersionRequired'));
                return false;
              }
              
              const isDuplicate = breakdown.some(bd => 
                bd.breakdownName.trim() === String(formData.breakdownName).trim()
              );
              
              if (isDuplicate) {
                setCreateBreakdownFormMessage(t('breakdownList.breakdownNameDuplicate', { name: formData.breakdownName }));
                return false;
              }
              
              performCreateBreakdown(
                String(formData.breakdownName).trim(),
                String(formData.breakdownVersion).trim(),
                true
              );
            }
          }
        ]}
        onClose={handleCloseCreateBreakdownDialog}
      />

      {/* 进度对话框 */}
      <ProgressDialog
        visible={progressDialogVisible}
        message={progressDialogMessage}
        progress={progressDialogProgress}
      />
    </div>
  );
};

export default Home;
